import React, { useEffect, useState, useRef, Component, lazy, Suspense } from 'react';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  componentDidCatch(error, info) { console.error('CHAT CRASH:', error.message, info); }
  static getDerivedStateFromError(error) { return { error: error.message }; }
  render() {
    if (this.state.error) return (
      <div style={{position:'fixed',inset:0,background:'#0A0E1A',color:'#FF4F6A',padding:20,fontSize:14,zIndex:999}}>
        <div style={{fontWeight:700,marginBottom:8}}>Erreur détectée:</div>
        <div>{this.state.error}</div>
        <button onClick={()=>this.setState({error:null})} style={{marginTop:16,padding:'8px 16px',background:'#00D4AA',border:'none',borderRadius:8,cursor:'pointer',color:'#000',fontWeight:700}}>Réessayer</button>
      </div>
    );
    return this.props.children;
  }
}
import { useMessageStore, usePaymentStore, useAuthStore } from '../store';
import { socketService } from '../services/socket';
import api, { messageAPI, paymentAPI } from '../services/api';
import { useLang } from '../i18n/i18n';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import E2E from '../services/e2eService';

// D: Imports dynamiques — code splitting automatique au build
// FaceAuthPage (face-api.js 8MB) et GroupCallPage (LiveKit 25MB)
// ne chargent que si l'utilisateur les utilise effectivement
const CallPage        = lazy(() => import('./CallPage').then(m => ({ default: m.CallPage })));
const IncomingCall    = lazy(() => import('./CallPage').then(m => ({ default: m.IncomingCall })));
const GroupCallPage   = lazy(() => import('./GroupCallPage').then(m => ({ default: m.GroupCallPage })));
const IncomingGroupCall = lazy(() => import('./GroupCallPage').then(m => ({ default: m.IncomingGroupCall })));
const TontinePage     = lazy(() => import('./TontinePage'));
const TontineCGU      = lazy(() => import('./TontinePage').then(m => ({ default: m.TontineCGU })));
const StoriesBar      = lazy(() => import('./StoriesBar'));
const QRPage          = lazy(() => import('./QRPage'));
const ServicesPage    = lazy(() => import('./ServicesPage'));
const MarketplacePage = lazy(() => import('./MarketplacePage'));
const SecurityPage    = lazy(() => import('./SecurityPage'));
const InternationalTransferPage = lazy(() => import('./InternationalTransferPage'));
const IntlTransferCGU = lazy(() => import('./InternationalTransferPage').then(m => ({ default: m.IntlTransferCGU })));
const CryptoPage      = lazy(() => import('./CryptoPage'));
const VoicePayPage    = lazy(() => import('./VoicePayPage').then(m => ({ default: m.VoicePayPage })));
const FaceAuthPage    = lazy(() => import('./FaceAuthPage').then(m => ({ default: m.FaceAuthPage })));
const ProfilePage     = lazy(() => import('./ProfilePage').then(m => ({ default: m.ProfilePage })));
const ChannelsPage    = lazy(() => import('./ChannelsPage'));
const BusinessApiPage = lazy(() => import('./BusinessApiPage'));
const StickerStorePage = lazy(() => import('./StickerStorePage'));
const VerifiedChannelPage = lazy(() => import('./VerifiedChannelPage'));
const CallsHistoryPage = lazy(() => import('./CallsHistoryPage').then(m => ({ default: m.CallsHistoryPage })));
const PinSetupPage    = lazy(() => import('./PinSetupPage').then(m => ({ default: m.PinSetupPage })));
const OnboardingPage  = lazy(() => import('./OnboardingPage'));

const ONBOARDING_KEY  = 'paychat_onboarding_done';
const TONTINE_CGU_KEY = 'paychat_tontine_cgu_accepted';
const INTL_CGU_KEY    = 'paychat_intl_transfer_cgu_accepted';

const C = {
  bg: '#0A0E1A', surface: '#111827', card: '#1a2235',
  border: '#1e2d45', accent: '#00D4AA', accentDim: '#00d4aa22',
  gold: '#F5C542', red: '#FF4F6A', text: '#E8EDF5', muted: '#6b7fa3',
};

const getCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const lang = navigator.language || '';
    if (tz.startsWith('America/') && (lang.includes('en-US') || lang.includes('en-CA'))) return '$';
    if (tz.startsWith('Europe/') && lang.includes('en-GB')) return 'GBP';
    if (tz.startsWith('Europe/') && !lang.startsWith('fr-CM')) return 'EUR';
    return 'FCFA';
  } catch { return 'FCFA'; }
};
const CUR = getCurrency();
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(parseFloat(n||0))) + ' ' + CUR;

let _bipAudio = null;
const playBip = () => {
  try {
    if (!_bipAudio) { _bipAudio = new Audio('/message.wav'); _bipAudio.volume = 0.5; }
    _bipAudio.currentTime = 0;
    _bipAudio.play().catch(() => {});
    if (navigator.vibrate) navigator.vibrate(50);
  } catch {}
};

function Avatar({ name='', color=C.accent, size=40, online, avatarUrl=null }) {
  const initials = name.split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase();
  return (
    <div style={{position:'relative',flexShrink:0}}>
      {avatarUrl ? (
        <img src={avatarUrl} alt={name} loading="lazy" style={{width:size,height:size,borderRadius:'50%',objectFit:'cover',border:`1.5px solid ${color}55`}}/>
      ) : (
        <div style={{width:size,height:size,borderRadius:'50%',background:`linear-gradient(135deg,${color}cc,${color}55)`,border:`1.5px solid ${color}55`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*0.35,fontWeight:700,color:'#fff'}}>
          {initials||'?'}
        </div>
      )}
      {online!==undefined&&<div style={{position:'absolute',bottom:1,right:1,width:9,height:9,borderRadius:'50%',background:online?C.accent:'#444',border:`2px solid ${C.bg}`}}/>}
    </div>
  );
}

const renderInner = (text, startKey=0) => {
  let key = startKey;
  const parts = [];
  let remaining = String(text);
  while (remaining.length > 0) {
    const bm = remaining.match(/^(.*?)\*([^*]+)\*(.*)/s);
    const im = remaining.match(/^(.*?)_([^_]+)_(.*)/s);
    const sm = remaining.match(/^(.*?)~([^~]+)~(.*)/s);
    const matches = [bm&&{idx:bm[1].length,match:bm,tag:'b'},im&&{idx:im[1].length,match:im,tag:'i'},sm&&{idx:sm[1].length,match:sm,tag:'s'}].filter(Boolean).sort((a,b)=>a.idx-b.idx);
    if (!matches.length) { parts.push(<span key={key++}>{remaining}</span>); break; }
    const {match,tag} = matches[0];
    if (match[1]) parts.push(<span key={key++}>{match[1]}</span>);
    if (tag==='b') parts.push(<strong key={key++}>{match[2]}</strong>);
    else if (tag==='i') parts.push(<em key={key++}>{match[2]}</em>);
    else parts.push(<del key={key++}>{match[2]}</del>);
    remaining = match[3];
  }
  return parts;
};

const renderText = (text) => {
  // Détecter les URLs et les rendre cliquables
  if (typeof text === 'string' && text.match(/https?:\/\//)) {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    const urls = parts.filter(p => /^https?:\/\//.test(p));
    return (<span>
      {parts.map((part, i) => /^https?:\/\//.test(part)
        ? <a key={i} href={part} target="_blank" rel="noopener noreferrer"
            style={{color:'#00D4AA',textDecoration:'underline',wordBreak:'break-all'}}
            onClick={e=>e.stopPropagation()}>{part}</a>
        : part)}
      {urls.length > 0 && <LinkPreview key={urls[0]} url={urls[0]} />}
    </span>);
  }
    if (!text) return null;
    const parts = [];
    let remaining = String(text);
    let key = 0;
    
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/^(.*?)\*([^*]+)\*(.*)/s);
      const italicMatch = remaining.match(/^(.*?)_([^_]+)_(.*)/s);
      const strikeMatch = remaining.match(/^(.*?)~([^~]+)~(.*)/s);
      
      const matches = [
        boldMatch && { idx: boldMatch[1].length, match: boldMatch, tag: 'b' },
        italicMatch && { idx: italicMatch[1].length, match: italicMatch, tag: 'i' },
        strikeMatch && { idx: strikeMatch[1].length, match: strikeMatch, tag: 's' },
      ].filter(Boolean).sort((a, b) => a.idx - b.idx);
      
      if (matches.length === 0) {
        parts.push(<span key={key++}>{remaining.split('\n').map((l,i,arr)=><span key={i}>{l}{i<arr.length-1&&<br/>}</span>)}</span>);
        break;
      }
      
      const { match, tag } = matches[0];
      if (match[1]) parts.push(<span key={key++}>{match[1].split('\n').map((l,i,arr)=><span key={i}>{l}{i<arr.length-1&&<br/>}</span>)}</span>);
      
      const inner = match[2];
      const innerRendered = inner.match(/[*_~]/) ? renderInner(inner, key) : inner;
      if (tag === 'b') parts.push(<strong key={key++}>{innerRendered}</strong>);
      else if (tag === 'i') parts.push(<em key={key++}>{innerRendered}</em>);
      else if (tag === 's') parts.push(<del key={key++}>{innerRendered}</del>);
      
      remaining = match[3];
    }
    return parts;
  }

const REACTIONS = ['👍','❤️','😂','😮','😢','🙏'];

// ── TTS : lire un message à voix haute ──────────────────
const speakMessage = (text, lang = 'fr-FR') => {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const clean = text.replace(/[*_~`]/g, '').substring(0, 500);
  const utt = new SpeechSynthesisUtterance(clean);
  utt.lang = lang;
  utt.rate = 0.95;
  utt.pitch = 1;
  window.speechSynthesis.speak(utt);
};

function MessageBubble({ msg, fromMe, onReply, onDelete, onReact, onForward, onEdit, decryptedText }) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const t = (k) => ({ delete:'Supprimer', edit:'Modifier', reply:'Répondre', forward:'Transférer', copy:'Copier', speak:'Écouter', stop:'Arrêter' }[k] || k);

  const handleSpeak = () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    if (msg.type !== 'text') return;
    const raw = decryptedText || msg.content;
    speakMessage(String(raw || ''));
    setIsSpeaking(true);
    setTimeout(() => setIsSpeaking(false), (String(raw||'').length * 60) + 1000);
  };
  const br = fromMe ? '16px 4px 16px 16px' : '4px 16px 16px 16px';
  const bg = fromMe ? 'linear-gradient(135deg,#003d2e,#002820)' : C.card;
  const border = `1px solid ${fromMe?C.accent+'44':C.border}`;

  const renderContent = () => {
    if (msg.type==='payment'||msg.type==='payment_request') {
      let amt = {}; 
      try { 
        const raw = msg.content;
        if (typeof raw === 'string') amt = JSON.parse(raw);
        else if (raw && typeof raw === 'object') amt = raw;
      } catch { amt={}; }
      return (
        <div style={{background:bg,border,borderRadius:16,padding:'12px 16px',maxWidth:240}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:24}}>💸</span>
            <div>
              <div style={{fontSize:11,color:C.muted}}>{fromMe?'Envoyé':'Reçu'}</div>
              <div style={{fontSize:20,fontWeight:800,color:fromMe?C.accent:C.gold}}>{parseFloat(amt.amount||0).toFixed(0)} {amt.currency==='EUR'?'FCFA':amt.currency||CUR}</div>
            </div>
          </div>
          {amt.note&&<div style={{fontSize:12,color:C.muted,marginTop:8,paddingTop:8,borderTop:`1px solid ${C.border}`}}>{amt.note}</div>}
        </div>
      );
    }
    if (msg.type==='image') {
      let data; try { data=typeof msg.content==='string'?JSON.parse(msg.content):msg.content; } catch { data={url:''}; }
      return (
        <div style={{borderRadius:br,overflow:'hidden',maxWidth:260,border}}>
          <img src={data.url} alt={data.caption||'Photo'} style={{width:'100%',maxHeight:220,objectFit:'cover',display:'block'}} loading="lazy"/>
          {data.caption&&<div style={{padding:'6px 10px',fontSize:13,color:C.text,background:bg}}>{data.caption}</div>}
        </div>
      );
    }
    if (msg.type==='video') {
      let data; try { data=typeof msg.content==='string'?JSON.parse(msg.content):msg.content; } catch { data={url:''}; }
      return (
        <div style={{borderRadius:br,overflow:'hidden',maxWidth:280,border}}>
          <video src={data.url} controls playsInline style={{width:'100%',maxHeight:200,display:'block',background:'#000'}}/>
          {data.caption&&<div style={{padding:'6px 10px',fontSize:13,color:C.text,background:bg}}>{data.caption}</div>}
        </div>
      );
    }
    if (msg.type==='document') {
      let data; try { data=typeof msg.content==='string'?JSON.parse(msg.content):msg.content; } catch { data={url:'',filename:''}; }
      if (!data || !data.url) return <div style={{background:bg,border,borderRadius:br,padding:'10px 16px',fontSize:13,color:C.muted}}>Document indisponible</div>;
      const ext = (data.filename||'').split('.').pop().toUpperCase();
      const size = data.bytes ? (data.bytes/1024/1024).toFixed(1)+'MB' : '0MB';
      const docUrl = data.url.startsWith('http') ? data.url : window.location.origin + data.url;
      return (
        <a href={docUrl} style={{display:'flex',alignItems:'center',gap:12,background:bg,border,borderRadius:br,padding:'12px 16px',maxWidth:260,cursor:'pointer',textDecoration:'none'}}>
          <div style={{width:40,height:48,borderRadius:8,background:C.accent+'22',border:`1px solid ${C.accent}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:C.accent,flexShrink:0}}>{ext}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{data.filename||'Document'}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>{size} · Appuyer pour ouvrir {/iPad|iPhone/.test(navigator.userAgent)?'(choisir Afficher)':''}</div>
          </div>
        </a>
      );
    }
    if (msg.type==='audio') {
      let data; try { data=typeof msg.content==='string'?JSON.parse(msg.content):msg.content; } catch { data={url:''}; }
      const isMp4Audio = data.url && (data.url.includes('.mp4') || (data.mimetype && data.mimetype.includes('mp4')));
      return (
        <div style={{background:bg,border,borderRadius:br,padding:'10px 12px',minWidth:220,maxWidth:280}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span>🎤</span>
            <audio
              controls
              playsInline
              preload="metadata"
              style={{flex:1,height:36,minWidth:160}}
              onError={(e) => { console.warn('Audio error:', e.target.error); }}
            >
              {isMp4Audio && <source src={data.url} type="audio/mp4"/>}
              {isMp4Audio && <source src={data.url} type="audio/aac"/>}
              {!isMp4Audio && <source src={data.url} type="audio/webm"/>}
              {!isMp4Audio && <source src={data.url} type="audio/ogg"/>}
              <source src={data.url}/>
            </audio>
          </div>
          {data.duration && <div style={{fontSize:10,color:C.muted,marginTop:4}}>{Math.round(data.duration)}s</div>}
        </div>
      );
    }
    if (msg.type==='sticker') {
      let data; try { data=typeof msg.content==='string'?JSON.parse(msg.content):msg.content; } catch { data={url:''}; }
      return (
        <img src={data.url} alt={data.name||'sticker'} style={{width:48,height:48,cursor:'pointer',borderRadius:6}}/>
      );
    }
    if (msg.type==='location') {
      let data; try { data=typeof msg.content==='string'?JSON.parse(msg.content):msg.content; } catch { data={}; }
      const mapsUrl = `https://www.google.com/maps?q=${data.lat},${data.lng}`;
      return (
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{textDecoration:'none',display:'block'}}>
          <div style={{background:bg,border,borderRadius:br,overflow:'hidden',minWidth:220,maxWidth:280,cursor:'pointer'}}>
            <div style={{background:'#1a2235',height:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <div style={{fontSize:40}}>📍</div>
            </div>
            <div style={{padding:'10px 14px'}}>
              <div style={{fontWeight:700,fontSize:13,color:C.text}}>{data.name||'Ma position'}</div>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>{data.lat?.toFixed(4)}, {data.lng?.toFixed(4)}</div>
              <div style={{fontSize:11,color:C.accent,marginTop:4}}>Appuyer pour ouvrir dans Maps</div>
            </div>
          </div>
        </a>
      );
    }
    if (msg.type==='poll') {
      let data; try { data=typeof msg.content==='string'?JSON.parse(msg.content):msg.content; } catch { data={}; }
      return <PollBubble pollId={data.poll_id} question={data.question} options={data.options} fromMe={fromMe} bg={bg} border={border} br={br} liveResults={msg._pollResults} />;
    }
    if (msg.type==='contact') {
      let data; try { data=typeof msg.content==='string'?JSON.parse(msg.content):msg.content; } catch { data={}; }
      return (
        <div style={{background:bg,border,borderRadius:br,padding:'12px 16px',minWidth:200}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <Avatar name={data.name||''} color={C.accent} size={40}/>
            <div>
              <div style={{fontWeight:700,fontSize:14}}>{data.name}</div>
              <div style={{fontSize:12,color:C.muted}}>@{data.username}</div>
            </div>
          </div>
          <div style={{fontSize:11,color:C.muted,marginTop:8,textAlign:'center'}}>👤 Contact PayChat</div>
        </div>
      );
    }
    return (
      <div style={{background:bg,border,borderRadius:br,padding:'10px 14px',fontSize:14,color:C.text,lineHeight:1.5,wordBreak:'break-word',maxWidth:280}}>
        {msg.reply_to&&(
          <div style={{background:'#ffffff11',borderLeft:`3px solid ${C.accent}`,borderRadius:4,padding:'4px 8px',marginBottom:8,fontSize:12,color:C.muted}}>
            ↩ {String(msg.reply_to.content||'').substring(0,50)}
          </div>
        )}
        {renderText(decryptedText || msg.content)}
      </div>
    );
  };

  return (
    <div style={{position:'relative'}}>
      {msg.reactions&&Object.keys(msg.reactions).length>0&&(
        <div style={{display:'flex',gap:4,marginBottom:4,justifyContent:fromMe?'flex-end':'flex-start',flexWrap:'wrap'}}>
          {Object.entries(msg.reactions).map(([emoji,count])=>(
            <span key={emoji} onClick={()=>onReact(msg.id,emoji)}
              style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'2px 6px',fontSize:12,cursor:'pointer'}}>
              {emoji} {count}
            </span>
          ))}
        </div>
      )}
      <div style={{display:'flex',alignItems:'flex-end',gap:4,flexDirection:fromMe?'row-reverse':'row'}}>
        {renderContent()}
        <button onClick={()=>setShowMenu(!showMenu)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:16,padding:'0 2px',flexShrink:0,opacity:0.6}}>⋮</button>
      </div>
      {showMenu&&(
        <div style={{position:'fixed',inset:0,zIndex:500}} onClick={()=>setShowMenu(false)}>
          <div style={{position:'fixed',[fromMe?'right':'left']:16,bottom:180,background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',minWidth:200,boxShadow:'0 8px 32px #00000088',zIndex:501}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',gap:4,padding:'8px 12px',borderBottom:`1px solid ${C.border}`}}>
              {msg.type==='text' && (
                <button onClick={()=>{handleSpeak();setShowMenu(false);}} title={isSpeaking?'Arrêter':'Écouter'}
                  style={{background:isSpeaking?'#FF4F6A22':'#00D4AA22',border:`1px solid ${isSpeaking?'#FF4F6A':'#00D4AA'}44`,borderRadius:8,padding:'4px 8px',cursor:'pointer',fontSize:16,color:isSpeaking?'#FF4F6A':'#00D4AA'}}>
                  {isSpeaking?'⏹️':'🔊'}
                </button>
              )}
              {REACTIONS.map(emoji=>(
                <button key={emoji} onClick={()=>{onReact(msg.id,emoji);setShowMenu(false);}} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',padding:'2px'}}>{emoji}</button>
              ))}
            </div>
            <button onClick={()=>{onDelete(msg.id);setShowMenu(false);}} style={{width:'100%',padding:'10px 16px',background:'none',border:'none',color:C.red,cursor:'pointer',textAlign:'left',fontSize:14,borderBottom:`1px solid ${C.border}`}}>{`🗑️ ${t('delete')}`}</button>
            {fromMe&&msg.type==='text'&&<button onClick={()=>{onEdit&&onEdit(msg);setShowMenu(false);}} style={{width:'100%',padding:'10px 16px',background:'none',border:'none',color:'#00D4AA',cursor:'pointer',textAlign:'left',fontSize:14,borderBottom:`1px solid ${C.border}`}}>{`✏️ ${t('edit')}`}</button>}
            <button onClick={()=>{onReply(msg);setShowMenu(false);}} style={{width:'100%',padding:'10px 16px',background:'none',border:'none',color:C.text,cursor:'pointer',textAlign:'left',fontSize:14,borderBottom:`1px solid ${C.border}`}}>{`↩ ${t('reply')}`}</button>
            <button onClick={()=>{onForward&&onForward(msg);setShowMenu(false);}} style={{width:'100%',padding:'10px 16px',background:'none',border:'none',color:C.text,cursor:'pointer',textAlign:'left',fontSize:14,borderBottom:`1px solid ${C.border}`}}>{`↗ ${t('forward')}`}</button>
            <button onClick={()=>{navigator.clipboard.writeText(typeof msg.content==='string'&&!msg.content.startsWith('{')?msg.content:'');setShowMenu(false);}} style={{width:'100%',padding:'10px 16px',background:'none',border:'none',color:C.text,cursor:'pointer',textAlign:'left',fontSize:14}}>{`📋 ${t('copy')}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteModal({ onClose }) {
  const inviteText = "Rejoins-moi sur PayChat — messagerie et paiements instantanes!\n\nhttps://zenvoi.app";
  return (
    <div style={{position:'fixed',inset:0,background:'#00000088',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:200}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:'20px 20px 0 0',padding:24,width:'100%',maxWidth:500}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
          <div style={{fontWeight:800,fontSize:18}}>Inviter un ami</div>
          <button onClick={onClose} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,width:30,height:30,cursor:'pointer',color:C.muted}}>x</button>
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px 16px',marginBottom:16,fontSize:13,color:C.muted,lineHeight:1.6}}>{inviteText}</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <button onClick={()=>window.open('https://wa.me/?text='+encodeURIComponent(inviteText),'_blank')} style={{padding:'12px',background:'#25D366',border:'none',borderRadius:12,cursor:'pointer',color:'#fff',fontWeight:700,fontSize:14}}>
            Partager via WhatsApp
          </button>
          <button onClick={()=>window.open('sms:?body='+encodeURIComponent(inviteText),'_blank')} style={{padding:'12px',background:C.accent,border:'none',borderRadius:12,cursor:'pointer',color:'#000',fontWeight:700,fontSize:14}}>
            Partager via SMS
          </button>
          <button onClick={()=>{navigator.clipboard.writeText('https://zenvoi.app');toast.success('Lien copie !');}} style={{padding:'12px',background:C.card,border:`1px solid ${C.border}`,borderRadius:12,cursor:'pointer',color:C.text,fontWeight:700,fontSize:14}}>
            Copier le lien
          </button>
        </div>
      </div>
    </div>
  );
}


function PayInlineModal({ contact, wallet, conversationId, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [step, setStep] = useState('amount');
  const payingRef = useRef(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [error, setError] = useState('');
  const balance = wallet?.wallet?.balance || 0;
  const fmtAmt = v => new Intl.NumberFormat('fr-FR').format(v);

  const handleSend = () => {
    const amt = parseInt(amount);
    if (!amt || amt < 100) return setError('Montant minimum : 100 FCFA');
    if (amt > balance) return setError('Solde insuffisant : ' + fmtAmt(balance) + ' FCFA');
    setError(''); setStep('pin');
  };

  const handleConfirm = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (payingRef.current) return; // Empêcher double clic
    console.log('PIN saisi:', pin, 'longueur:', pin.length);
    if (pin.length < 6) return setError('PIN incomplet — ' + pin.length + '/6 chiffres saisis');
    payingRef.current = true;
    setStep('loading'); setError('');
    try {
      console.log('PayInlineModal - conversationId:', conversationId, 'contact_id:', contact?.contact_id);
      const payload = {
        receiver_id: contact?.contact_id,
        amount: parseInt(amount),
        pin,
        currency: 'XAF',
        conversation_id: conversationId || null
      };
      console.log('Payload envoyé:', JSON.stringify(payload));
      const res = await paymentAPI.sendPayment(payload);
      console.log('Réponse paiement complète:', JSON.stringify(res));
      console.log('success value:', res?.success, typeof res?.success);
      // L'intercepteur axios retourne response.data directement — res EST le body JSON
      if (res?.success === true) { 
        onSuccess(parseInt(amount), res?.data?.transaction);
        return;
      } else { 
        setError(res?.error || res?.message || 'Erreur paiement'); 
        setStep('pin');
        payingRef.current = false;
      }
    } catch(e) { 
      console.log('Erreur paiement catch complet:', JSON.stringify(e));
      // L'intercepteur axios retourne error.response.data directement
      // donc e EST le body JSON de l'erreur, pas un objet Error standard
      if (e?.success === true || e?.data?.transaction?.id) {
        onSuccess(parseInt(amount));
        return;
      }
      // Fallback: vérifier si une transaction vient d'être créée (< 15 sec)
      try {
        const check = await paymentAPI.getTransactions({ limit: 1 });
        const txs = check?.data || check;
        const last = Array.isArray(txs) ? txs[0] : txs?.transactions?.[0];
        if (last?.status === 'completed' && Date.now() - new Date(last.created_at).getTime() < 15000) {
          onSuccess(parseInt(amount));
          return;
        }
      } catch {}
      // Si pas de réponse réseau du tout (WebSocket error, etc.) — vérifier via API
      if (!e.response) {
        try {
          const check = await paymentAPI.getTransactions();
          const lastTx = check.data?.data?.[0];
          if (lastTx && lastTx.status === 'completed' && 
              Date.now() - new Date(lastTx.created_at).getTime() < 30000) {
            onSuccess(parseInt(amount));
            return;
          }
        } catch {}
      }
      // Debug iPhone : afficher l'erreur complète
      const debugInfo = JSON.stringify({
        status: e.response?.status,
        data: e.response?.data,
        msg: e.message,
        code: e.code
      });
      console.error('ERREUR PAIEMENT DETAIL:', debugInfo);
      setError(e.response?.data?.error || e.message || 'Erreur paiement');
      payingRef.current = false;
      setStep('pin');
      payingRef.current = false;
    }
  };

  const C2 = { bg:'#111827', card:'#1a2235', border:'#1e2d45', text:'#E8EDF5', muted:'#6b7fa3', accent:'#00D4AA', red:'#FF4F6A' };

  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,background:'#00000099',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:200}}>
      <div style={{background:C2.bg,border:'1px solid '+C2.border,borderRadius:'20px 20px 0 0',padding:24,width:'100%',maxWidth:500,paddingBottom:40}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div style={{fontWeight:800,fontSize:18,color:C2.text}}>{"💸 Envoyer à " + (contact?.contact_name||'')}</div>
          <button onClick={onClose} style={{background:C2.card,border:'1px solid '+C2.border,borderRadius:8,width:32,height:32,cursor:'pointer',color:C2.muted,fontSize:16}}>✕</button>
        </div>
        <div style={{background:'#0A0F1E',borderRadius:12,padding:'10px 14px',marginBottom:20,display:'flex',justifyContent:'space-between'}}>
          <span style={{color:C2.muted,fontSize:13}}>Solde disponible</span>
          <span style={{color:C2.accent,fontWeight:800,fontSize:15}}>{fmtAmt(balance)} FCFA</span>
        </div>
        {step==='amount'&&(
          <>
            <div style={{marginBottom:12}}>
              <div style={{color:C2.muted,fontSize:12,fontWeight:600,marginBottom:8}}>MONTANT (FCFA)</div>
              <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="Ex: 5000" autoFocus
                style={{width:'100%',background:C2.card,border:'1px solid '+C2.border,borderRadius:12,padding:'14px 16px',color:C2.text,fontSize:24,fontWeight:700,outline:'none',boxSizing:'border-box',textAlign:'center'}}/>
            </div>
            <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
              {[500,1000,2000,5000,10000].map(m=>(
                <button key={m} onClick={()=>setAmount(String(m))}
                  style={{flex:1,minWidth:55,padding:'8px 4px',background:amount==m?'#00D4AA22':C2.card,border:'1px solid '+(amount==m?C2.accent:C2.border),borderRadius:8,color:amount==m?C2.accent:C2.muted,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                  {m>=1000?(m/1000)+'k':m}
                </button>
              ))}
            </div>
            {error&&<div style={{color:C2.red,fontSize:13,marginBottom:12,textAlign:'center'}}>{error}</div>}
            <button onClick={handleSend} style={{width:'100%',padding:14,background:'linear-gradient(90deg,#00D4AA,#00b894)',border:'none',borderRadius:12,color:'#000',fontWeight:800,fontSize:16,cursor:'pointer'}}>
              Continuer →
            </button>
          </>
        )}
        {step==='pin'&&(
          <>
            <div style={{textAlign:'center',marginBottom:16}}>
              <div style={{color:C2.text,fontSize:16,marginBottom:4}}>{"Montant : "}<strong style={{color:C2.accent}}>{fmtAmt(parseInt(amount))} FCFA</strong></div>
              <div style={{color:C2.muted,fontSize:13}}>Entrez votre PIN pour confirmer</div>
            </div>
            <input type="password" inputMode="numeric" maxLength={6} value={pin} onChange={e=>setPin(e.target.value)} placeholder="● ● ● ● ● ●" autoFocus
              style={{width:'100%',background:C2.card,border:'1px solid '+C2.border,borderRadius:12,padding:'14px 16px',color:C2.text,fontSize:28,fontWeight:700,outline:'none',boxSizing:'border-box',textAlign:'center',letterSpacing:12,marginBottom:16}}/>
            {error&&<div style={{color:C2.red,fontSize:13,marginBottom:12,textAlign:'center'}}>{error}</div>}
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>{setStep('amount');setPin('');setError('');}}
                style={{flex:1,padding:14,background:C2.card,border:'1px solid '+C2.border,borderRadius:12,color:C2.muted,fontWeight:700,fontSize:14,cursor:'pointer'}}>← Retour</button>
              <button 
                onPointerUp={handleConfirm}
                onTouchEnd={e=>{e.preventDefault();}}
                disabled={step==='loading'}
                style={{flex:2,padding:14,background:step==='loading'?'#1a2235':'linear-gradient(90deg,#00D4AA,#00b894)',border:'none',borderRadius:12,color:step==='loading'?'#6b7fa3':'#000',fontWeight:800,fontSize:16,cursor:step==='loading'?'not-allowed':'pointer'}}>
                {step==='loading'?'⏳ Envoi...':'Confirmer'}
              </button>
            </div>
          </>
        )}
        {step==='loading'&&(
          <div style={{textAlign:'center',padding:40}}>
            <div style={{fontSize:40,marginBottom:16}}>⏳</div>
            <div style={{color:C2.text,fontSize:16}}>Envoi en cours...</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ChatPage() {
  const { t } = useLang();
  // Forcer re-render quand langue change
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const handler = () => forceUpdate(n => n + 1);
    window.addEventListener('paychat-lang-change', handler);
    return () => window.removeEventListener('paychat-lang-change', handler);
  }, []);
  const { conversations, activeConversationId, messages, typingUsers, onlineUsers, fetchConversations, setActiveConversation, sendMessage, createConversation } = useMessageStore();
  const { wallet, fetchWallet } = usePaymentStore();
  const { user, logout } = useAuthStore();
  const [input, setInput] = useState('');
  const [showFormat, setShowFormat] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showTontine, setShowTontine] = useState(false);
  const [showTontineCGU, setShowTontineCGU] = useState(false);
  const [showIntlCGU, setShowIntlCGU] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [showMarketplace, setShowMarketplace] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showIntlTransfer, setShowIntlTransfer] = useState(false);
  const [showCrypto, setShowCrypto] = useState(false);
  const [editingMsg, setEditingMsg] = useState(null); // {id, content}
  const [showInvite, setShowInvite] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchTimerRef = useRef(null);

  // ── Badge icône app (API Badging PWA) ──────────────────
  const updateAppBadge = (count) => {
    try {
      if (count > 0) {
        if (navigator.setAppBadge) navigator.setAppBadge(count);
      } else {
        if (navigator.clearAppBadge) navigator.clearAppBadge();
      }
    } catch(e) {}
  };
  const [imagePreview, setImagePreview] = useState(null);
  const [decryptedCache, setDecryptedCache] = useState({});
  const [docViewer, setDocViewer] = useState(null);
  const [imageCaption, setImageCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isDictating, setIsDictating] = useState(false);
  const dictationRef = useRef(null);
  const [recSeconds, setRecSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const wakeLockRef = useRef(null);
  const [replyTo, setReplyTo] = useState(null);
  const [view, setView] = useState('list');
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem(ONBOARDING_KEY));
  // Utilisateurs existants : si des conversations chargent, ce n'est pas une première connexion
  useEffect(() => {
    if (showOnboarding && conversations.length > 0) {
      localStorage.setItem(ONBOARDING_KEY, '1');
      setShowOnboarding(false);
    }
  }, [conversations.length]);
  const [activeCall, setActiveCall] = useState(null);
  const [activeGroupCall, setActiveGroupCall] = useState(null);
  const [incomingGroupCall, setIncomingGroupCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showVoicePay, setShowVoicePay] = useState(false);
  const [showFaceAuth, setShowFaceAuth] = useState(false);
  const [faceAuthMode, setFaceAuthMode] = useState('verify');
  const [showProfile, setShowProfile] = useState(false);
  const [showCallsHistory, setShowCallsHistory] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showMediaMenu, setShowMediaMenu] = useState(false);
  const [showStickers, setShowStickers] = useState(false);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [convOptions, setConvOptions] = useState(null);
  const longPressRef = useRef(null);
  const [ephemeralDuration, setEphemeralDuration] = useState(0); // 0 = désactivé
  const ephemeralDurationRef = useRef(0);
  const [showEphemeralMenu, setShowEphemeralMenu] = useState(false);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showChannels, setShowChannels] = useState(false);
  const [showBusinessApi, setShowBusinessApi] = useState(false);
  const [showStickerStore, setShowStickerStore] = useState(false);
  const [showVerifiedChannel, setShowVerifiedChannel] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [addMemberIds, setAddMemberIds] = useState([]);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const mediaRecRef = useRef(null);
  const recTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageFileRef = useRef(null);
  const cameraRef = useRef(null);
  const videoFileRef = useRef(null);
  const videoCaptureRef = useRef(null);
  const docFileRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const selectionRef = useRef({start:0, end:0});
  const prevMsgCount = useRef(0);
  const ringtoneRef = useRef(null);

  const [localMessages, set_messages] = useState({});

  // ── STT : dicter un message ──────────────────────────────
  const startDictation = () => {
    if (!('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
      toast.error('Dictée vocale non supportée sur ce navigateur');
      return;
    }
    if (isDictating) {
      dictationRef.current?.stop();
      setIsDictating(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    dictationRef.current = rec;
    rec.lang = 'fr-FR';
    rec.continuous = false;
    rec.interimResults = true;
    rec.onstart = () => setIsDictating(true);
    rec.onresult = (e) => {
      const txt = e.results[e.results.length-1][0].transcript;
      if (e.results[e.results.length-1].isFinal) {
        setInput(prev => (prev ? prev + ' ' : '') + txt);
        setIsDictating(false);
        if (activeConversationId) socketService.sendTypingStart(activeConversationId);
      }
    };
    rec.onerror = (e) => {
      setIsDictating(false);
      if (e.error !== 'aborted' && e.error !== 'no-speech')
        toast.error('Erreur dictée: ' + e.error);
    };
    rec.onend = () => setIsDictating(false);
    rec.start();
  };

  const applyFormat = (tag) => {
    const markers = tag === 'b' ? '*' : tag === 'i' ? '_' : '~';
    const start = selectionRef.current.start;
    const end = selectionRef.current.end;
    const selected = input.slice(start, end);
    let newText;
    if (start !== end && selected) {
      newText = input.slice(0, start) + markers + selected + markers + input.slice(end);
    } else if (input.trim()) {
      // Pas de sélection — wrapper tout le texte
      newText = markers + input.trim() + markers;
    } else {
      newText = markers + markers;
    }
    setInput(newText);
    setTimeout(() => {
      try {
        const ta = textareaRef.current;
        if (ta) { ta.focus(); ta.setSelectionRange(start+1, end+1); }
      } catch(e) {}
    }, 50);
  };

  const startRingtone = () => {
    try {
      if (ringtoneRef.current) return;
      let audio = document.getElementById('paychat-ringtone');
      if (!audio) {
        audio = document.createElement('audio');
        audio.id = 'paychat-ringtone';
        audio.src = '/sounds/ringtone.wav';
        audio.loop = true;
        audio.volume = 1.0;
        document.body.appendChild(audio);
      }
      audio.currentTime = 0;
      ringtoneRef.current = audio;

      // Essayer de jouer
      const tryPlay = () => {
        audio.play().then(() => {
          console.log('Ringtone playing OK');
        }).catch(e => {
          console.warn('Ringtone blocked:', e.message);
          // Vibration sur mobile
          if (navigator.vibrate) {
            const vib = setInterval(() => navigator.vibrate([400,200,400,200,400]), 1500);
            audio._vibInterval = vib;
          }
          // Retry sur prochain touch
          const retryPlay = () => {
            audio.play().catch(()=>{});
            document.removeEventListener('touchstart', retryPlay);
            document.removeEventListener('click', retryPlay);
          };
          document.addEventListener('touchstart', retryPlay, { once: true });
          document.addEventListener('click', retryPlay, { once: true });
        });
      };
      tryPlay();

      if ('wakeLock' in navigator) {
        navigator.wakeLock.request('screen').then(lock => { wakeLockRef.current = lock; }).catch(()=>{});
      }
    } catch(e) { console.warn('startRingtone error:', e.message); }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      if (ringtoneRef.current._vibInterval) {
        clearInterval(ringtoneRef.current._vibInterval);
      }
      if (navigator.vibrate) navigator.vibrate(0); // Stopper vibration
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(()=>{});
      wakeLockRef.current = null;
    }
  };

  // Pré-remplir l'input quand on édite un message
  useEffect(() => {
    if (editingMsg) setInput(editingMsg.content || '');
    else setInput('');
  }, [editingMsg]);

  const activeConv = conversations.find(c=>c.id===activeConversationId);
  const currentMessages = messages[activeConversationId] || [];
  const typing = typingUsers[activeConversationId]||{};

  // Débloquer l'audio iOS au premier clic
  useEffect(() => {
    const unlock = () => {
      const audio = document.getElementById('paychat-ringtone') || document.createElement('audio');
      audio.id = 'paychat-ringtone';
      audio.src = '/sounds/ringtone.wav';
      audio.loop = true;
      audio.volume = 1.0;
      if (!document.getElementById('paychat-ringtone')) document.body.appendChild(audio);
      // Jouer 0.001s puis pauser pour débloquer iOS
      audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(()=>{});
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('click', unlock);
    };
    document.addEventListener('touchstart', unlock, { once: true });
    document.addEventListener('click', unlock, { once: true });
  }, []);

  // A3+B4: Ouvrir l'app depuis une notification push (app était fermée)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const callDataStr = params.get('calldata');
    const callAction = params.get('callaction');
    const convId = params.get('conv');

    // Notification de message → ouvrir la conversation
    if (convId && !callDataStr) {
      window.history.replaceState({}, '', '/');
      setTimeout(() => { setActiveConversation(convId); setView('chat'); }, 1500);
      return;
    }

    // Notification d'appel
    if (!callDataStr || !callAction || callAction === 'view') return;
    window.history.replaceState({}, '', '/');
    try {
      const cd = JSON.parse(atob(decodeURIComponent(callDataStr)));
      if (callAction === 'reject') {
        setTimeout(() => socketService.rejectCall(cd.callId), 2500);
      } else if (callAction === 'accept') {
        setTimeout(() => {
          setActiveCall({
            callId: cd.callId,
            callerId: cd.callerId,
            callerName: cd.callerName,
            callType: cd.callType || 'audio',
            conversationId: cd.conversationId,
            incoming: true,
          });
        }, 2000);
      }
    } catch(e) {}
  }, []);

  // iOS foreground reconnect
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem('access_token');
        if (token && !socketService.isConnected()) {
          socketService.connect(token);
        }
        fetchConversations();
        // Recalculer le badge quand l'app revient au premier plan
        const totalUnread = useMessageStore.getState().conversations
          .reduce((sum, c) => sum + parseInt(c.unread_count || 0, 10), 0);
        updateAppBadge(totalUnread);
      } else {
        // App en arrière-plan — badge déjà mis à jour via useEffect conversations
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(()=>{ 
    fetchConversations(); 
    fetchWallet();
    // Écouter les accusés de lecture
    if (socketService.socket) {
      socketService.socket.on('message:receipt', ({ conversation_id, message_id, status }) => {
        if (status === 'read') {
          useMessageStore.getState().markConversationRead(conversation_id);
          // Mettre à jour is_read sur tous les messages de la conversation
          const { messages: storeMessages, updateMessage } = useMessageStore.getState();
          const convMsgs = storeMessages[conversation_id] || [];
          convMsgs.forEach(m => {
            if (!m.is_read) {
              updateMessage(conversation_id, m.id, { is_read: true });
            }
          });
        }
      });
      // Rafraîchir le wallet quand un paiement est reçu
      socketService.socket.on('poll:updated', ({ poll_id, results }) => {
        const { messages: storeMessages, updateMessage } = useMessageStore.getState();
        let found = false;
        for (const [convId, msgs] of Object.entries(storeMessages)) {
          (msgs || []).forEach(m => {
            if (m.type === 'poll') {
              try {
                const data = typeof m.content === 'string' ? JSON.parse(m.content) : m.content;
                if (data && data.poll_id === poll_id) {
                  updateMessage(convId, m.id, { _pollResults: results });
                  found = true;
                }
              } catch {}
            }
          });
        }
        if (!found) console.log('poll:updated - message non trouvé pour poll_id:', poll_id);
      });

      socketService.socket.on('wallet:updated', () => {
        fetchWallet();
      });
    } 
    // Push enregistré manuellement via bouton 🔔
    // Enregistrer les handlers d'appel - toujours, socket ou pas
    socketService.setCallHandlers({
      onRinging: ({ callId }) => {
        // Mettre à jour le callId quand le serveur confirme
        setActiveCall(prev => {
          if (prev) return {...prev, callId};
          return prev;
        });
        toast.dismiss('calling');
      },
      onIncoming: (callData) => {
        console.log('Appel entrant:', callData);
        // Ignorer si un appel est deja en cours
        setIncomingCall(prev => {
          if (prev) {
            // Rejeter automatiquement le nouvel appel
            socketService.socket?.emit('call:reject', { callId: callData.callId });
            return prev;
          }
          startRingtone();
          return callData;
        });
      },
      onEnded: ({ callId }) => {
        // Toujours stopper la sonnerie quand un appel se termine
        stopRingtone();
        setActiveCall(prev => {
          if (!prev) return null;
          if (prev.callId === callId || prev.callId?.startsWith('temp_') || !callId) {
            return null;
          }
          return prev;
        });
        // Toujours nettoyer l'appel entrant - le callId peut ne pas correspondre
        setIncomingCall(prev => {
          if (!prev) return null;
          // Si pas de callId ou callId correspond ou callId temporaire → nettoyer
          if (!callId || prev.callId === callId || prev.callId?.startsWith('temp_')) {
            stopRingtone();
            return null;
          }
          // Même si callId différent → nettoyer quand même (sécurité)
          stopRingtone();
          return null;
        });
      },
      onRejected: ({ callId }) => {
        // Vérifier que c'est bien l'appel actif
        setActiveCall(prev => {
          if (prev && (prev.callId === callId || prev.callId?.startsWith('temp_'))) {
            toast.error('Appel refusé');
            return null;
          }
          return prev;
        });
      },
      onAccepted: () => { toast.dismiss('ringing'); },
      onSignal: null,
    });

    // Écouter les appels de groupe via socket direct
    const setupGroupCallListeners = (sock) => {
      if (!sock) return;
      sock.off('call:group-started');
      sock.off('call:incoming-group');
      sock.on('call:group-started', ({ callId, callType, conversationId }) => {
        console.log('GROUP STARTED:', callId);
        setActiveGroupCall({ callId, callType, conversationId, isInitiator: true });
      });
      sock.on('call:incoming-group', (groupCall) => {
        console.log('GROUP INCOMING:', groupCall);
        setIncomingGroupCall(groupCall);
      });
    };
    setupGroupCallListeners(socketService.socket);
    // Réenregistrer si le socket se reconnecte
    const reconnectInterval = setInterval(() => {
      if (socketService.socket && !socketService.socket._groupListenersSet) {
        socketService.socket._groupListenersSet = true;
        setupGroupCallListeners(socketService.socket);
      }
    }, 2000);

    // Écouter les messages du Service Worker
    const handleSWMessage = (event) => {
      if (event.data?.type === 'INCOMING_CALL_RING') startRingtone();
      if (event.data?.type === 'STOP_RING') stopRingtone();
      // B4: Clic sur notification de message → ouvrir la conversation directement
      if (event.data?.type === 'OPEN_CONVERSATION') {
        const { conversationId } = event.data;
        if (conversationId) {
          setActiveConversation(conversationId);
          setView('chat');
        }
      }
      if (event.data?.type === 'CALL_ACTION') {
        const { action, callData } = event.data;
        if (action === 'accept' && callData?.callId) {
          socketService.acceptCall(callData.callId);
          setIncomingCall(null);
          stopRingtone();
        } else if (action === 'reject' && callData?.callId) {
          socketService.rejectCall(callData.callId);
          setIncomingCall(null);
          stopRingtone();
        }
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
      stopRingtone();
      clearInterval(reconnectInterval);
    };
  },[]);

  // localMessages supprimé — on utilise directement messages du store (mis à jour par socket)

  useEffect(()=>{
    messagesEndRef.current?.scrollIntoView({behavior:'auto'});
    if (prevMsgCount.current > 0 && currentMessages.length > prevMsgCount.current) {
      const last = currentMessages[currentMessages.length-1];
      if (last && last.sender_id !== user?.id && !last.isTemp) playBip();
    }
    prevMsgCount.current = currentMessages.length;
  },[currentMessages]);

  // Décryptage E2E non-bloquant — batch sur tous les messages courants
  useEffect(() => {
    const privateKey = E2E.getPrivateKey(user?.id);
    if (!privateKey) return;
    const toDecrypt = currentMessages.filter(
      m => m.type === 'text' && m.content && E2E.isEncrypted(m.content)
    );
    if (!toDecrypt.length) return;
    let cancelled = false;
    Promise.all(
      toDecrypt.map(async m => {
        if (decryptedCache[m.id]) return null;
        try { return [m.id, await E2E.decrypt(m.content, privateKey)]; }
        catch { return [m.id, '[Message chiffré]']; }
      })
    ).then(pairs => {
      if (cancelled) return;
      const entries = pairs.filter(Boolean);
      if (entries.length) setDecryptedCache(prev => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => { cancelled = true; };
  }, [currentMessages]);

  // Appel automatique au démarrage (après 5s)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (user && 'Notification' in window && Notification.permission === 'default') {
        registerPush();
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [user?.id]);

  const registerPush = async () => {
    if (registerPush.running) return;
    registerPush.running = true;
    try {
      if (!('serviceWorker' in navigator)||!('PushManager' in window)||!('Notification' in window)) {
        // Afficher modal d'installation iOS
        useAuthStore.getState().setShowInstallGuide && useAuthStore.getState().setShowInstallGuide(true);
        set_showInstallGuide(true);
        return;
      }
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission!=='granted') {
        toast.error('Permission refusée. Activez les notifications dans les paramètres.', {duration:4000});
        return;
      }
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const keyRes = await fetch('/api/push/vapid-key',{headers:{Authorization:'Bearer '+token}});
      const keyData = await keyRes.json();
      if (!keyData.success) return;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        // Convertir la clé VAPID base64url en Uint8Array
        const vapidKey = keyData.publicKey;
        const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
        const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: outputArray
        });
      }

      const res = await fetch('/api/push/subscribe',{
        method:'POST',
        headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},
        body:JSON.stringify({subscription:sub})
      });
      const data = await res.json();
      if (data.success) toast.success('🔔 Notifications activées !', {duration:3000});
    } catch(e) { console.warn('Push registration error:', e.message); } finally { registerPush.running = false; }
  };

  const openConv = (id) => {
    setActiveConversation(id);
    setView('chat');
    // Marquer les messages comme lus
    setTimeout(() => {
      const msgs = useMessageStore.getState().messages[id] || [];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && socketService.socket) {
        socketService.socket.emit('message:read', {
          conversation_id: id,
          message_id: lastMsg.id,
        });
      }
    }, 600);
  };

  const startCall = (callType) => {
    // Appel de groupe
    if (activeConv?.type === 'group') {
      const sock = socketService.socket;
      if (sock) {
        sock.emit('call:initiate-group', { conversationId: activeConversationId, callType });
        toast('📞 Appel de groupe en cours...', {duration:3000});
      } else {
        // Reconnecter et réessayer
        const token = localStorage.getItem('access_token');
        if (token) {
          socketService.connect(token);
          toast('🔄 Reconnexion...', {duration:2000});
          setTimeout(() => {
            socketService.socket?.emit('call:initiate-group', { conversationId: activeConversationId, callType });
          }, 2000);
        }
      }
      return;
    }
    if (!activeConv?.contact_id) return;
    if (!socketService.isConnected()) {
      const token = localStorage.getItem('access_token');
      if (token) {
        socketService.connect(token);
        toast('🔄 Reconnexion...', { duration: 2000 });
        setTimeout(() => startCallDirect(callType), 2500);
      } else {
        toast.error('Non connecté');
      }
      return;
    }
    startCallDirect(callType);
  };

  const startCallDirect = (callType) => {
    if (!activeConv?.contact_id) return;
    const callData = {
      callId: null,
      targetUserId: activeConv.contact_id,
      conversationId: activeConversationId,
      callType,
      contactName: activeConv.contact_name,
      incoming: false,
    };
    // E3: Réutiliser l'élément paychat-ringtone partagé — évite deux instances Audio concurrentes avec CallPage
    let outgoingAudio = document.getElementById('paychat-ringtone');
    if (!outgoingAudio) {
      outgoingAudio = document.createElement('audio');
      outgoingAudio.id = 'paychat-ringtone';
      outgoingAudio.src = '/sounds/ringtone.wav';
      outgoingAudio.loop = true;
      document.body.appendChild(outgoingAudio);
    }
    outgoingAudio.currentTime = 0;
    outgoingAudio.volume = 0.6;
    outgoingAudio.play().catch(() => {});
    
    // Initier l'appel
    socketService.initiateCall(activeConv.contact_id, activeConversationId, callType);
    let resolvedCallId = null;

    // Écouter call:ringing pour avoir le vrai callId
    const onRingingOnce = ({ callId }) => {
      resolvedCallId = callId;
      callData.callId = callId;
      outgoingAudio.pause();
      setActiveCall({...callData, callId, status: 'ringing'});
      socketService.socket?.off('call:ringing', onRingingOnce);
      toast.dismiss('calling');
    };
    socketService.socket?.on('call:ringing', onRingingOnce);

    socketService.onCallEvent('call:unavailable', ({ reason }) => {
      outgoingAudio.pause();
      setActiveCall(null);
      toast.error(reason || 'Utilisateur indisponible');
      socketService.offCallEvent('call:unavailable');
      socketService.socket?.off('call:ringing', onRingingOnce);
      toast.dismiss('calling');
    });

    // Afficher bouton raccrocher pendant l'attente du callId
    toast((t) => (
      <div style={{display:'flex',alignItems:'center',gap:12}}>
        <span>📞 Appel en cours...</span>
        <button onClick={()=>{
          toast.dismiss(t.id);
          outgoingAudio.pause();
          if (resolvedCallId) {
            socketService.socket?.emit('call:end', { callId: resolvedCallId });
          }
          socketService.socket?.emit('call:cancel', { targetUserId: callData.targetUserId });
          socketService.offCallEvent('call:unavailable');
          setActiveCall(null);
        }} style={{background:'#FF4F6A',border:'none',borderRadius:8,padding:'4px 10px',color:'#fff',cursor:'pointer',fontWeight:700}}>
          Raccrocher
        </button>
      </div>
    ), { id: 'calling', duration: 30000 });
  };

  const acceptCall = () => {
    if (!incomingCall) return;
    stopRingtone();
    stopRingtone();
    socketService.acceptCall(incomingCall.callId);
    setActiveCall({ ...incomingCall, incoming: true });
    setIncomingCall(null);
  };

  const rejectCall = () => {
    if (!incomingCall) return;
    stopRingtone();
    socketService.rejectCall(incomingCall.callId);
    setIncomingCall(null);
  };
  const goBack = () => { setView('list'); setReplyTo(null); };

  const loadGroupParticipants = async (convId) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/messages/conversations/${convId}/participants`, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const data = await res.json();
      if (data.success) setGroupParticipants(data.data);
    } catch(e) {}
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Quitter ce groupe ?')) return;
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`/api/messages/conversations/${activeConversationId}/leave`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Vous avez quitté le groupe');
      setShowGroupInfo(false);
      await fetchConversations();
      setView('list');
    } catch(e) { toast.error('Erreur'); }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!window.confirm(`Retirer ${userName} du groupe ?`)) return;
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`/api/messages/conversations/${activeConversationId}/participants/${userId}`, {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + token }
      });
      toast.success(`${userName} retiré du groupe`);
      await loadGroupParticipants(activeConversationId);
    } catch(e) { toast.error('Erreur'); }
  };

  const handleAddMembers = async () => {
    if (addMemberIds.length === 0) { toast.error('Sélectionnez au moins un membre'); return; }
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`/api/messages/conversations/${activeConversationId}/participants`, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: addMemberIds })
      });
      toast.success('Membres ajoutés !');
      setAddMemberIds([]);
      setShowAddMembers(false);
      await loadGroupParticipants(activeConversationId);
    } catch(e) { toast.error('Erreur ajout membres'); }
  };

  const handleToggleAdminOnly = async (currentMode) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`/api/messages/conversations/${activeConversationId}/settings`, {
        method: 'PATCH',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_only: !currentMode })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(!currentMode ? '🔒 Seuls les admins peuvent écrire' : '🔓 Tout le monde peut écrire');
        await loadGroupParticipants(activeConversationId);
        await fetchConversations();
      }
    } catch(e) { toast.error('Erreur'); }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) { toast.error('Donnez un nom au groupe'); return; }
    if (groupMembers.length < 2) { toast.error('Ajoutez au moins 2 membres'); return; }
    const conv = await createConversation(groupMembers, 'group', groupName.trim());
    if (conv) {
      setShowCreateGroup(false);
      setGroupName('');
      setGroupMembers([]);
      toast.success('Groupe créé !');
      setActiveConversation(conv.id);
      setView('chat');
    }
  };

  const toggleGroupMember = (userId) => {
    setGroupMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Mettre à jour le badge à chaque changement de conversations
  React.useEffect(() => {
    const totalUnread = conversations.reduce((sum, c) => sum + parseInt(c.unread_count || 0, 10), 0);
    updateAppBadge(totalUnread);
    // Mettre à jour le titre de l'onglet aussi
    if (totalUnread > 0) {
      document.title = `(${totalUnread}) PayChat`;
    } else {
      document.title = 'PayChat';
    }
  }, [conversations]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    // Annuler la requête précédente
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (q.length < 2) { setSearchResults([]); return; }
    // Debounce 400ms — attend que l'utilisateur arrête de taper
    searchTimerRef.current = setTimeout(async () => {
      try {
        const r = await messageAPI.searchUsers(q);
        setSearchResults(r.data || []);
      } catch(e) {
        setSearchResults([]);
      }
    }, 400);
  };

  // ── Import contacts téléphone (Contact Picker API) ────────
  const importPhoneContacts = async () => {
    try {
      if (!('contacts' in navigator && 'ContactsManager' in window)) {
        toast.error('Votre navigateur ne supporte pas la lecture des contacts');
        return;
      }
      const contacts = await navigator.contacts.select(['name', 'tel'], { multiple: true });
      if (!contacts || contacts.length === 0) return;

      // Chercher chaque contact dans PayChat
      let found = 0;
      for (const contact of contacts) {
        for (const tel of (contact.tel || [])) {
          const clean = tel.replace(/\s/g, '').replace(/[^+\d]/g, '');
          try {
            const r = await messageAPI.searchUsers(clean);
            if (r.data && r.data.length > 0) {
              found++;
              // Ajouter aux résultats de recherche
              setSearchResults(prev => {
                const ids = prev.map(u => u.id);
                const newUsers = r.data.filter(u => !ids.includes(u.id));
                return [...prev, ...newUsers];
              });
            }
          } catch {}
        }
      }
      if (found > 0) {
        toast.success(`${found} contact(s) trouvé(s) sur PayChat !`);
      } else {
        toast('Aucun de vos contacts n\'est encore sur PayChat', { icon: 'ℹ️' });
      }
    } catch(e) {
      if (e.name !== 'AbortError') toast.error('Erreur lecture contacts');
    }
  };

  const startConv = async (id) => {
    const conv = await useMessageStore.getState().createConversation([id]);
    if (conv) { setSearchQuery(''); setSearchResults([]); await fetchConversations(); openConv(conv.id); }
  };



  const getExpiresAt = () => {
    const dur = ephemeralDurationRef.current;
    if (!dur) return null;
    return new Date(Date.now() + dur * 1000).toISOString();
  };

  const handleSend = async () => {
    // Si on est en mode édition
    if (editingMsg) {
      const text = input.trim();
      if (text) await handleEdit(editingMsg.id, text);
      setInput('');
      return;
    }
    // Bloquer si mode admin-only et pas admin
    if (activeConv?.type === 'group' && activeConv?.admin_only) {
      const myRole = groupParticipants.find(p => p.user_id === user?.id)?.role;
      if (myRole !== 'admin') {
        toast.error('🔒 Seuls les admins peuvent écrire dans ce groupe');
        return;
      }
    }
    if (!input.trim()||!activeConversationId) return;
    const content = input;
    setInput('');
    setReplyTo(null);
    await sendMessage(activeConversationId, content, undefined, undefined, getExpiresAt());
  };

  const handleReact = async (msgId, emoji) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/messages/'+msgId+'/react',{method:'POST',headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},body:JSON.stringify({emoji})});
      const data = await res.json();
      if (data.success) {
        useMessageStore.getState().updateMessage(activeConversationId, msgId, {reactions: data.data});
      } else {
        console.warn('Erreur reaction:', data.error);
      }
    } catch(e) { toast.error('Erreur: '+e.message); }
  };

  const archiveConversation = async (convId, archive=true) => {
    try {
      await api.post('/messages/conversations/'+convId+'/archive', { archived: archive });
      await fetchConversations();
      if (!archive) setShowArchived(false);
      toast.success(archive ? '📦 Archivée' : '📬 Désarchivée');
    } catch { toast.error('Erreur'); }
  };

  const muteConversation = async (convId, mute=true) => {
    try {
      await api.post('/messages/conversations/'+convId+'/mute', { muted: mute });
      await fetchConversations();
      toast.success(mute ? '🔕 Muette' : '🔔 Notifications activées');
    } catch { toast.error('Erreur'); }
  };

  const setEphemeral = async (seconds) => {
    setEphemeralDuration(seconds);
    ephemeralDurationRef.current = seconds;
    setShowEphemeralMenu(false);
    if (seconds === 0) {
      toast.success('Messages éphémères désactivés');
    } else {
      const labels = {300:'5 min',3600:'1 heure',86400:'24 heures',604800:'7 jours'};
      toast.success('Messages éphémères : ' + (labels[seconds] || seconds+'s'));
    }
  };

  const handleEdit = async (msgId, newContent) => {
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`/api/messages/${msgId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content: newContent })
      });
      useMessageStore.getState().updateMessage(activeConversationId, msgId, { content: newContent, is_edited: true });
      setEditingMsg(null);
    } catch(e) { toast.error('Erreur modification'); }
  };

  const handleDelete = async (msgId) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch('/api/messages/'+msgId,{method:'DELETE',headers:{Authorization:'Bearer '+token}});
      const data = await res.json();
      if (data.success) {
        useMessageStore.getState().updateMessage(activeConversationId, msgId, {is_deleted: true});
        toast.success('Message supprimé');
      } else {
        toast.error('Erreur: ' + data.error);
      }
    } catch(e) { toast.error('Erreur: '+e.message); }
  };

  const handleForwardTo = async (convId) => {
    if (!forwardMsg) return;
    try {
      const token = localStorage.getItem('access_token');
      const msgContent = typeof forwardMsg.content === 'object' ? JSON.stringify(forwardMsg.content) : forwardMsg.content;
      const payload = {content: msgContent, type: forwardMsg.type||'text'};
      const res = await fetch('/api/messages/conversations/'+convId+'/messages', {
        method:'POST',
        headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},
        body:JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) { toast.success('Message transfere !'); setForwardMsg(null); }
      else toast.error('Erreur: ' + data.error);
    } catch(e) { toast.error('Erreur transfert'); }
  };

  const sendSticker = async (stickerName) => {
    if (!activeConversationId) { toast.error('Pas de conversation active'); return; }
    try {
      const token = localStorage.getItem('access_token');
      const url = '/stickers/pack1/' + stickerName;
      const content = JSON.stringify({ url, name: stickerName.replace('.svg','') });
      const res = await fetch('/api/messages/conversations/'+activeConversationId+'/messages', {
        method:'POST',
        headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},
        body:JSON.stringify({content, type:'sticker'})
      });
      const data = await res.json();
      if (data.success && data.data) {
        setTimeout(() => useMessageStore.getState().addMessage(data.data), 300);
      }
      setShowStickers(false);
    } catch(e) { toast.error('Erreur: ' + e.message, {duration:5000}); }
  };

  const shareLocation = async () => {
    if (!activeConversationId) return;
    const token = localStorage.getItem('access_token');

    const sendLocationMsg = async (lat, lng, name) => {
      try {
        const locationData = { lat, lng, name };
        const res = await fetch('/api/messages/conversations/'+activeConversationId+'/messages', {
          method:'POST',
          headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},
          body:JSON.stringify({content: JSON.stringify(locationData), type:'location'})
        });
        const data = await res.json();
        if (data.success) {
          toast.success('📍 Position partagée !');
          if (data.data) useMessageStore.getState().addMessage(data.data);
        } else {
          toast.error('Erreur: ' + data.error);
        }
      } catch(e) { toast.error('Erreur envoi position'); }
    };

    if (!navigator.geolocation) {
      toast.error('Géolocalisation non supportée');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        await sendLocationMsg(pos.coords.latitude, pos.coords.longitude, 'Ma position');
      },
      async (err) => {
        toast.error('GPS indisponible: ' + (err.message || 'erreur'));
      },
      { timeout: 15000, maximumAge: 0, enableHighAccuracy: true }
    );
  };

  const shareContact = async (contactUser) => {
    if (!activeConversationId) return;
    try {
      const token = localStorage.getItem('access_token');
      const content = JSON.stringify({name:contactUser.display_name,username:contactUser.username,id:contactUser.id});
      await fetch('/api/messages/conversations/'+activeConversationId+'/messages',{method:'POST',headers:{Authorization:'Bearer '+token,'Content-Type':'application/json'},body:JSON.stringify({content,type:'contact'})});
      toast.success('Contact partage !');
    } catch { toast.error('Erreur partage'); }
  };

  const decryptMsg = async (msg) => {
    if (!msg.content || msg.type !== 'text') return msg.content;
    if (decryptedCache[msg.id]) return decryptedCache[msg.id];
    if (!E2E.isEncrypted(msg.content)) return msg.content;
    try {
      const privateKey = E2E.getPrivateKey(user?.id);
      if (!privateKey) return '[Message chiffré]';
      const decrypted = await E2E.decrypt(msg.content, privateKey);
      setDecryptedCache(prev => ({...prev, [msg.id]: decrypted}));
      return decrypted;
    } catch(e) { return '[Message chiffré]'; }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Copier le fichier avant de vider l'input
    const fileCopy = new File([file], file.name || 'photo.jpg', { type: file.type || 'image/jpeg' });
    imageFileRef.current = fileCopy;
    // Compresser avant preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 2048;
        let w=img.width, h=img.height;
        if(w>h){if(w>MAX){h=h*MAX/w;w=MAX;}}else{if(h>MAX){w=w*MAX/h;h=MAX;}}
        canvas.width=w; canvas.height=h;
        canvas.getContext('2d').drawImage(img,0,0,w,h);
        canvas.toBlob(blob=>{
          if(blob) {
            imageFileRef.current = new File([blob],'photo.jpg',{type:'image/jpeg'});
            setImagePreview({file:imageFileRef.current,url:URL.createObjectURL(blob)});
          }
        },'image/jpeg',0.95);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(fileCopy);
    e.target.value='';
  };

  const sendMedia = async (file, type) => {
    if (!file || !activeConversationId) return;
    setIsUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('conversationId', activeConversationId);
      const res = await fetch('/api/media/upload', { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd });
      if (!res.ok) { toast.error('Erreur HTTP: ' + res.status); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      if (data.data) setTimeout(() => useMessageStore.getState().addMessage(data.data), 300);
      toast.success(type === 'video' ? '📹 Vidéo envoyée !' : type === 'document' ? '📄 Document envoyé !' : '📷 Photo envoyée !');
    } catch(e) { toast.error('Erreur envoi: ' + e.message); }
    finally { setIsUploading(false); }
  };

  const sendImage = async () => {
    if (!imagePreview||!activeConversationId) return;
    setIsUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) { toast.error('Non connecte'); setIsUploading(false); return; }
      const fd = new FormData();
      const f = imageFileRef.current||imagePreview.file;
      if (!f) { toast.error('Fichier introuvable'); setIsUploading(false); return; }
      fd.append('file',f); fd.append('conversationId',activeConversationId);
      if (imageCaption) fd.append('caption',imageCaption);
      const res = await fetch('/api/media/upload',{method:'POST',headers:{Authorization:'Bearer '+token},body:fd});
      if (!res.ok) { toast.error('Erreur HTTP: '+res.status); setIsUploading(false); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      URL.revokeObjectURL(imagePreview.url);
      setImagePreview(null); setImageCaption('');
      // Ajouter le message image au store local
      if (data.data) {
        setTimeout(() => {
          useMessageStore.getState().addMessage(data.data);
        }, 500);
      }
      toast.success('Photo envoyee !');
    } catch(err) { toast.error('Erreur: '+(err.message||String(err))); }
    finally { setIsUploading(false); }
  };

  const startRec = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Microphone non supporté sur ce navigateur');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({audio:true});
      // iOS Safari supporte mp4/aac, Android/Chrome supporte webm
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/ogg') ? 'audio/ogg'
        : '';
      const rec = new MediaRecorder(stream, mimeType ? {mimeType} : {});
      const chunks=[];
      rec.ondataavailable=e=>{ if(e.data&&e.data.size>0) chunks.push(e.data); };
      rec.onstop=()=>{
        if (chunks.length === 0) { toast.error('Enregistrement vide'); stream.getTracks().forEach(t=>t.stop()); return; }
        const blob = new Blob(chunks, {type: mimeType || 'audio/mp4'});
        setAudioBlob(blob);
        stream.getTracks().forEach(t=>t.stop());
      };
      // iOS : timeslice 1000ms pour forcer la collecte régulière sans coupure
      rec.start(1000);
      // Garder l'écran actif pendant l'enregistrement (évite coupure iOS)
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch(e) {}
      mediaRecRef.current=rec; setIsRecording(true); setRecSeconds(0);
      recTimerRef.current=setInterval(()=>setRecSeconds(s=>{ if(s>=119){stopRec();return s;} return s+1; }),1000);
    } catch(e) { toast.error('Microphone indisponible: ' + e.message); }
  };

  const stopRec = () => {
    const rec = mediaRecRef.current;
    clearInterval(recTimerRef.current);
    setIsRecording(false);
    try { wakeLockRef.current?.release(); wakeLockRef.current = null; } catch(e) {}
    if (rec && rec.state === 'recording') {
      try { rec.requestData(); } catch(e) {}
      setTimeout(() => { try { rec.stop(); } catch(e) {} }, 200);
    }
  };
  const cancelRec = () => { mediaRecRef.current?.stop(); clearInterval(recTimerRef.current); setIsRecording(false); setAudioBlob(null); };

  const sendAudio = async () => {
    if (!audioBlob||!activeConversationId) return;
    try {
      const fd=new FormData();
      const ext=audioBlob.type.includes('mp4')?'mp4':audioBlob.type.includes('ogg')?'ogg':'webm';
      const filename = 'voice.'+ext;
      console.log('sendAudio - type:', audioBlob.type, 'size:', audioBlob.size, 'ext:', ext);
      fd.append('file', audioBlob, filename);
      fd.append('conversationId', activeConversationId);
      const token=localStorage.getItem('access_token');
      const res=await fetch('/api/media/upload',{method:'POST',headers:{Authorization:'Bearer '+token},body:fd});
      const data=await res.json();
      console.log('sendAudio - response:', JSON.stringify(data));
      if (!data.success) throw new Error(data.error);
      setAudioBlob(null);
      // Ajouter le message audio au store local
      if (data.data) {
        setTimeout(() => {
          useMessageStore.getState().addMessage(data.data);
        }, 500);
      }
      toast.success('Vocal envoyé !');
    } catch(err) { toast.error(err.message||'Erreur'); }
  };

  const colors=['#7C3AED','#0EA5E9','#EC4899','#F97316','#10B981','#6366F1'];
  const getColor=id=>colors[(id?.charCodeAt(0)||0)%colors.length];
  const timeAgo=date=>{
  try{
    const d=new Date(date);
    const now=new Date();
    const diff=now-d;
    if(diff<60000) return 'maintenant';
    if(diff<3600000) return Math.floor(diff/60000)+'min';
    if(d.toDateString()===now.toDateString()) return d.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'});
    const yesterday=new Date(now); yesterday.setDate(now.getDate()-1);
    if(d.toDateString()===yesterday.toDateString()) return 'Hier '+d.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'});
    return d.toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'});
  }catch{return '';}
};
  const lastSeenText=(dateStr,isOnline)=>{
    if(isOnline) return 'En ligne';
    if(!dateStr) return '';
    try{
      const diff=Date.now()-new Date(dateStr).getTime();
      if(diff<60000) return 'Vu à l\'instant';
      if(diff<3600000) return 'Vu il y a '+Math.floor(diff/60000)+' min';
      if(diff<86400000) return 'Vu à '+new Date(dateStr).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'});
      return 'Vu le '+new Date(dateStr).toLocaleDateString('fr-FR',{day:'2-digit',month:'short'});
    }catch{return '';}
  };
  const lastMsg=conv=>{
    if(conv.last_message_type==='payment'||conv.last_message_type==='payment_request') return 'Paiement';
    if(conv.last_message_type==='image') return 'Photo';
    if(conv.last_message_type==='audio') return 'Vocal';
    if(conv.last_message_type==='contact') return 'Contact';
    if(conv.last_message_type==='location') return 'Position partagee';
    if(conv.last_message_content?.startsWith?.('{')) return 'Fichier';
    const txt = conv.last_message_content||'Nouvelle conversation';
    return txt.replace(/[*_~]/g, '');
  };

  // Afficher l'écran d'appel actif
  if (activeGroupCall) return <Suspense fallback={null}><GroupCallPage call={activeGroupCall} onEnd={()=>setActiveGroupCall(null)}/></Suspense>;
  if (activeCall) return (
    <Suspense fallback={null}>
      <CallPage
        call={activeCall}
        onEnd={()=>setActiveCall(null)}
        onUpgrade={(g)=>{ setActiveCall(null); setActiveGroupCall(g); }}
      />
    </Suspense>
  );

  const S={fontFamily:"'Sora',sans-serif",color:C.text};
  const CSS=`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0;-webkit-touch-callout:none;-webkit-user-select:none;user-select:none;}input,textarea{-webkit-user-select:text;user-select:text;}html,body{overflow:hidden;width:100%;height:100%;background:#0A0E1A;overscroll-behavior:none;}#root{height:100%;overflow:hidden;padding-top:env(safe-area-inset-top,0px);}input,textarea,select{font-size:16px!important;}::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:#1e2d45;border-radius:4px}input::placeholder{color:#6b7fa3}@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}`;

  // ========== MODAL CRÉATION GROUPE ==========
  const contacts = conversations.filter(c => c.type === 'direct' && c.contact_id);

  if (showCreateGroup) return (
    <div style={{position:'fixed',inset:0,background:C.bg,zIndex:300,display:'flex',flexDirection:'column',fontFamily:"'Sora',sans-serif"}}>
      <div style={{padding:'16px 20px',background:C.surface,borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:16}}>
        <button onClick={()=>{setShowCreateGroup(false);setGroupName('');setGroupMembers([]);}} style={{background:'none',border:'none',color:C.muted,fontSize:22,cursor:'pointer'}}>←</button>
        <div style={{fontWeight:800,fontSize:18,color:C.text}}>Nouveau groupe</div>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:20}}>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:12,color:C.muted,marginBottom:8,fontWeight:600}}>NOM DU GROUPE</div>
          <input
            value={groupName}
            onChange={e=>setGroupName(e.target.value)}
            placeholder="Ex: Famille, Équipe ZENVOI..."
            style={{width:'100%',background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px 16px',color:C.text,fontSize:15,outline:'none',boxSizing:'border-box'}}
          />
        </div>
        <div style={{fontSize:12,color:C.muted,marginBottom:12,fontWeight:600}}>MEMBRES ({groupMembers.length} sélectionnés)</div>
        {contacts.map(conv => {
          const selected = groupMembers.includes(conv.contact_id);
          return (
            <div key={conv.contact_id} onClick={()=>toggleGroupMember(conv.contact_id)}
              style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderRadius:12,marginBottom:8,background:selected?C.accentDim:C.card,border:`1px solid ${selected?C.accent:C.border}`,cursor:'pointer'}}>
              <Avatar name={conv.contact_name||''} color={getColor(conv.contact_id)} size={42} avatarUrl={conv.contact_avatar_url}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:15,color:C.text}}>{conv.contact_name}</div>
                <div style={{fontSize:12,color:C.muted}}>{onlineUsers[conv.contact_id]?'En ligne':'Hors ligne'}</div>
              </div>
              <div style={{width:24,height:24,borderRadius:'50%',border:`2px solid ${selected?C.accent:C.border}`,background:selected?C.accent:'transparent',display:'flex',alignItems:'center',justifyContent:'center'}}>
                {selected&&<span style={{color:'#000',fontSize:14,fontWeight:800}}>✓</span>}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{padding:20,background:C.surface,borderTop:`1px solid ${C.border}`}}>
        <button onClick={handleCreateGroup}
          style={{width:'100%',padding:'14px',background:`linear-gradient(90deg,${C.accent},#00b894)`,border:'none',borderRadius:14,cursor:'pointer',color:'#000',fontWeight:800,fontSize:16}}>
          Créer le groupe {groupMembers.length>0?`(${groupMembers.length+1} membres)`:''}
        </button>
      </div>
    </div>
  );

  // ===== PAGE GESTION GROUPE =====
  const isAdmin = groupParticipants.find(p => p.user_id === user?.id)?.role === 'admin';
  const adminOnly = conversations.find(c => c.id === activeConversationId)?.admin_only;

  if (showChannels) return <Suspense fallback={null}><ChannelsPage onClose={()=>setShowChannels(false)} onOpenChannel={(ch)=>{setShowChannels(false);setActiveConversation(ch.id);setView('chat');}}/></Suspense>;
  if (showBusinessApi) return <Suspense fallback={null}><BusinessApiPage onClose={()=>setShowBusinessApi(false)}/></Suspense>;
  if (showStickerStore) return <Suspense fallback={null}><StickerStorePage onClose={()=>setShowStickerStore(false)}/></Suspense>;
  if (showVerifiedChannel) return <Suspense fallback={null}><VerifiedChannelPage onClose={()=>setShowVerifiedChannel(false)}/></Suspense>;

  if (forwardMsg) return (
    <div style={{position:'fixed',inset:0,background:C.bg,zIndex:400,display:'flex',flexDirection:'column',fontFamily:"'Sora',sans-serif"}}>
      <div style={{padding:'16px 20px',background:C.surface,borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:16}}>
        <button onClick={()=>setForwardMsg(null)} style={{background:'none',border:'none',color:C.muted,fontSize:22,cursor:'pointer'}}>←</button>
        <div style={{fontWeight:800,fontSize:18,color:C.text}}>Transférer vers...</div>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:16}}>
        {conversations.map(conv => {
          const name = conv.type==='group' ? conv.name : conv.contact_name;
          return (
            <div key={conv.id} onClick={()=>handleForwardTo(conv.id)}
              style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:C.card,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:8,cursor:'pointer'}}>
              <Avatar name={name||''} color={getColor(conv.contact_id||conv.id)} size={42} avatarUrl={conv.contact_avatar_url}/>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:15,color:C.text}}>{name}</div>
                <div style={{fontSize:12,color:C.muted}}>{conv.type==='group'?'Groupe':'Direct'}</div>
              </div>
              <span style={{color:C.accent,fontSize:20}}>↗</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (showGroupInfo) return (
    <div style={{position:'fixed',inset:0,background:C.bg,zIndex:300,display:'flex',flexDirection:'column',fontFamily:"'Sora',sans-serif"}}>
      <div style={{padding:'16px 20px',background:C.surface,borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:16}}>
        <button onClick={()=>{setShowGroupInfo(false);setShowAddMembers(false);setAddMemberIds([]);}} style={{background:'none',border:'none',color:C.muted,fontSize:22,cursor:'pointer'}}>←</button>
        <div style={{fontWeight:800,fontSize:18,color:C.text}}>{activeConv?.name}</div>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:20}}>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:16,marginBottom:16,textAlign:'center'}}>
          <div style={{width:64,height:64,borderRadius:'50%',background:C.accentDim,display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,margin:'0 auto 12px'}}>👥</div>
          <div style={{fontWeight:700,fontSize:18,color:C.text}}>{activeConv?.name}</div>
          <div style={{fontSize:13,color:C.muted,marginTop:4}}>{groupParticipants.length} membres</div>
        </div>

        {isAdmin&&(
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:16,marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontWeight:600,fontSize:14,color:C.text}}>🔒 Messages admin uniquement</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>Seuls les admins peuvent écrire</div>
              </div>
              <button onClick={()=>handleToggleAdminOnly(adminOnly)}
                style={{background:adminOnly?C.accent:'#ffffff22',border:'none',borderRadius:20,padding:'6px 16px',cursor:'pointer',color:adminOnly?'#000':C.muted,fontWeight:700,fontSize:12}}>
                {adminOnly?'Activé':'Désactivé'}
              </button>
            </div>
          </div>
        )}

        <div style={{fontSize:12,color:C.muted,marginBottom:8,fontWeight:600}}>MEMBRES</div>
        {groupParticipants.map(p=>(
          <div key={p.user_id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:C.card,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:8}}>
            <Avatar name={p.display_name||''} color={getColor(p.user_id)} size={40}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:14,color:C.text}}>{p.display_name}{p.user_id===user?.id?' (Vous)':''}</div>
              <div style={{fontSize:11,color:p.role==='admin'?C.accent:C.muted}}>{p.role==='admin'?'Admin':'Membre'}</div>
            </div>
            {isAdmin&&p.user_id!==user?.id&&(
              <button onClick={()=>handleRemoveMember(p.user_id, p.display_name)}
                style={{background:'#FF4F6A22',border:`1px solid #FF4F6A44`,borderRadius:8,padding:'4px 10px',cursor:'pointer',color:'#FF4F6A',fontSize:12}}>
                Retirer
              </button>
            )}
          </div>
        ))}

        {isAdmin&&!showAddMembers&&(
          <button onClick={()=>setShowAddMembers(true)}
            style={{width:'100%',padding:'12px',background:C.accentDim,border:`1px solid ${C.accent}44`,borderRadius:12,cursor:'pointer',color:C.accent,fontWeight:700,fontSize:14,marginTop:8}}>
            ➕ Ajouter des membres
          </button>
        )}

        {showAddMembers&&(
          <div style={{marginTop:12}}>
            <div style={{fontSize:12,color:C.muted,marginBottom:8,fontWeight:600}}>SÉLECTIONNER À AJOUTER</div>
            {conversations.filter(c=>c.type==='direct'&&c.contact_id&&!groupParticipants.find(p=>p.user_id===c.contact_id)).map(conv=>(
              <div key={conv.contact_id} onClick={()=>setAddMemberIds(prev=>prev.includes(conv.contact_id)?prev.filter(id=>id!==conv.contact_id):[...prev,conv.contact_id])}
                style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:addMemberIds.includes(conv.contact_id)?C.accentDim:C.card,border:`1px solid ${addMemberIds.includes(conv.contact_id)?C.accent:C.border}`,borderRadius:12,marginBottom:8,cursor:'pointer'}}>
                <Avatar name={conv.contact_name||''} color={getColor(conv.contact_id)} size={36}/>
                <div style={{flex:1,fontSize:14,color:C.text,fontWeight:600}}>{conv.contact_name}</div>
                <div style={{width:20,height:20,borderRadius:'50%',border:`2px solid ${addMemberIds.includes(conv.contact_id)?C.accent:C.border}`,background:addMemberIds.includes(conv.contact_id)?C.accent:'transparent',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {addMemberIds.includes(conv.contact_id)&&<span style={{color:'#000',fontSize:12,fontWeight:800}}>✓</span>}
                </div>
              </div>
            ))}
            {addMemberIds.length>0&&(
              <button onClick={handleAddMembers}
                style={{width:'100%',padding:'12px',background:`linear-gradient(90deg,${C.accent},#00b894)`,border:'none',borderRadius:12,cursor:'pointer',color:'#000',fontWeight:800,fontSize:14,marginTop:8}}>
                Ajouter ({addMemberIds.length})
              </button>
            )}
          </div>
        )}

        <button onClick={handleLeaveGroup}
          style={{width:'100%',padding:'12px',background:'#FF4F6A22',border:`1px solid #FF4F6A44`,borderRadius:12,cursor:'pointer',color:'#FF4F6A',fontWeight:700,fontSize:14,marginTop:16}}>
          🚪 Quitter le groupe
        </button>
      </div>
    </div>
  );

  const BottomNav = () => {
  const { t: tNav } = useLang();
  return (
    <div style={{position:'fixed',bottom:0,left:0,right:0,background:C.surface,borderTop:`1px solid ${C.border}`,display:'flex',zIndex:50,paddingBottom:'env(safe-area-inset-bottom)'}}>
      {[
        {icon:'💬', label:tNav('messages'), action:()=>setView('list')},
        {icon:'📢', label:tNav('channels'), action:()=>setShowChannels(true)},
        {icon:'👛', label:tNav('wallet'), action:()=>window.location.href='/wallet'},
        {icon:'👤', label:tNav('profile'), action:()=>setShowProfile(true)},
      ].map(({icon,label,action}) => (
        <button key={label} onClick={action}
          style={{flex:1,padding:'10px 4px',background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
          <span style={{fontSize:20}}>{icon}</span>
          <span style={{fontSize:10,color:C.muted,fontWeight:600}}>{label}</span>
        </button>
      ))}
    </div>
  );
  };

  if (view==='list') return (
    <>
    {showOnboarding && (
      <ErrorBoundary>
        <Suspense fallback={<div style={{position:'fixed',inset:0,background:'#0A0E1A',zIndex:2000}}/>}>
          <OnboardingPage onDone={()=>{ localStorage.setItem(ONBOARDING_KEY,'1'); setShowOnboarding(false); }}/>
        </Suspense>
      </ErrorBoundary>
    )}
    {showVoicePay && <VoicePayPage onClose={()=>setShowVoicePay(false)} conversations={conversations}/>}
    {showTontineCGU && <TontineCGU onAccept={()=>{localStorage.setItem(TONTINE_CGU_KEY,'1');setShowTontineCGU(false);setShowTontine(true);}} onRefuse={()=>setShowTontineCGU(false)}/>}
    {showTontine && <TontinePage onClose={()=>setShowTontine(false)}/>}
    {showQR && <QRPage onClose={()=>setShowQR(false)}/>}
    {showServices && <ServicesPage onClose={()=>setShowServices(false)}/>}
    {showMarketplace && <MarketplacePage onClose={()=>setShowMarketplace(false)}/>}
    {showSecurity && <SecurityPage onClose={()=>setShowSecurity(false)}/>}
    {incomingGroupCall && <IncomingGroupCall call={incomingGroupCall}
      onAccept={()=>{setActiveGroupCall({...incomingGroupCall,incoming:true});setIncomingGroupCall(null);}}
      onReject={()=>setIncomingGroupCall(null)}/>}
    {showIntlCGU && <IntlTransferCGU onAccept={()=>{localStorage.setItem(INTL_CGU_KEY,'1');setShowIntlCGU(false);setShowIntlTransfer(true);}} onRefuse={()=>setShowIntlCGU(false)}/>}
    {showIntlTransfer && <InternationalTransferPage onClose={()=>setShowIntlTransfer(false)}/>}
    {showCrypto && <CryptoPage onClose={()=>setShowCrypto(false)}/>}
    {showPinSetup && <PinSetupPage onClose={()=>setShowPinSetup(false)} onSuccess={()=>setShowPinSetup(false)}/>}
    {showProfile && <Suspense fallback={null}><ProfilePage onClose={()=>setShowProfile(false)}/></Suspense>}
    {showCallsHistory && <CallsHistoryPage onClose={()=>setShowCallsHistory(false)} user={user}/>}
    {docViewer && (
      <div style={{position:'fixed',inset:0,background:'#0A0E1A',zIndex:500,display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'16px 20px',background:'#111827',borderBottom:`1px solid #1e2d45`}}>
          <button onClick={()=>setDocViewer(null)} style={{background:'none',border:'none',color:'#6b7fa3',fontSize:22,cursor:'pointer'}}>←</button>
          <div style={{flex:1,fontSize:14,fontWeight:600,color:'#E8EDF5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{docViewer.filename||'Document'}</div>
          <a href={docViewer.url} download={docViewer.filename} style={{background:'#00D4AA',color:'#000',border:'none',borderRadius:8,padding:'6px 12px',fontSize:12,fontWeight:700,textDecoration:'none'}}>⬇ Télécharger</a>
        </div>
        <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:20,padding:24}}>
          <div style={{fontSize:64}}>📄</div>
          <div style={{fontSize:16,fontWeight:600,color:'#E8EDF5',textAlign:'center'}}>{docViewer.filename}</div>
          <div style={{fontSize:13,color:'#6b7fa3'}}>{docViewer.bytes ? (docViewer.bytes/1024/1024).toFixed(1)+' MB' : ''}</div>
          <a href={docViewer.url} target='_blank' rel='noopener noreferrer' style={{background:'linear-gradient(90deg,#00D4AA,#00b894)',color:'#000',borderRadius:12,padding:'14px 28px',fontSize:15,fontWeight:700,textDecoration:'none'}}>Ouvrir le document</a>
        </div>
      </div>
    )}
    {/* IncomingCall géré dans le return principal */}
    {showFaceAuth && <FaceAuthPage mode={faceAuthMode} onSuccess={()=>{setShowFaceAuth(false);toast.success('Identité vérifiée !');}} onCancel={()=>setShowFaceAuth(false)}/>}
    <div style={{...S,position:'fixed',inset:0,background:C.bg,display:'flex',flexDirection:'column',overflow:'hidden'}}>
      <style>{CSS}</style>
      {showInvite&&<InviteModal onClose={()=>setShowInvite(false)}/>}
      <div style={{padding:'12px 16px',paddingTop:59,background:C.surface,borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div onClick={()=>setShowProfile(true)} style={{width:36,height:36,borderRadius:'50%',background:C.accent,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:800,color:'#000',cursor:'pointer',flexShrink:0}}>{(user?.display_name||'U')[0].toUpperCase()}</div>
          <div style={{display:'flex',alignItems:'baseline'}}>
            <span style={{fontSize:18,fontWeight:800,color:C.accent,letterSpacing:'-0.5px'}}>PAY</span>
            <span style={{fontSize:18,fontWeight:800,color:C.text,letterSpacing:'-0.5px',position:'relative',left:-3}}>CHAT</span>
          </div>
          <Link to="/wallet" style={{background:C.accentDim,border:`1px solid ${C.accent}44`,borderRadius:20,padding:'4px 10px',color:C.accent,fontSize:12,fontWeight:700,textDecoration:'none',flexShrink:0}}>
            💰 {wallet?new Intl.NumberFormat('fr-FR').format(parseFloat(wallet.wallet?.balance||0))+' FCFA':'…'}
          </Link>
        </div>
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          <button onClick={registerPush} style={{background:'none',border:'none',color:C.muted,fontSize:20,cursor:'pointer',padding:'4px'}} title="Activer notifications">🔔</button>
          <div style={{position:'relative'}}>
            <button onClick={()=>setShowMenu(m=>!m)} style={{background:'none',border:'none',color:C.muted,fontSize:22,cursor:'pointer',padding:'4px'}}>⋮</button>
            {showMenu&&(
              <>
              <div style={{position:'fixed',inset:0,zIndex:299}} onClick={()=>setShowMenu(false)}/>
              <div style={{position:'absolute',right:0,top:'110%',background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:8,zIndex:300,minWidth:230,boxShadow:'0 8px 24px #00000066',maxHeight:'70vh',overflowY:'auto'}}>
                <div style={{fontSize:11,color:C.muted,fontWeight:700,padding:'6px 14px',letterSpacing:1}}>MESSAGERIE</div>
                <div onClick={()=>{setShowMenu(false);setShowCreateGroup(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>👥 Créer un groupe</div>
                <div onClick={()=>{setShowMenu(false);setShowChannels(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>📢 Canaux</div>
                <div onClick={()=>{setShowMenu(false);setShowBusinessApi(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>⚙️ Business API</div>
                <div onClick={()=>{setShowMenu(false);setShowStickerStore(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>🎨 Boutique Stickers</div>
                <div onClick={()=>{setShowMenu(false);setShowVerifiedChannel(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>✓ Canal Vérifié</div>
                <div onClick={()=>{setShowMenu(false);setShowCallsHistory(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>📋 Historique appels</div>
                <div onClick={()=>{setShowMenu(false);setShowInvite(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>➕ Inviter un contact</div>
                <div style={{borderTop:`1px solid ${C.border}`,margin:'4px 0'}}/>
                <div style={{fontSize:11,color:C.muted,fontWeight:700,padding:'6px 14px',letterSpacing:1}}>PAIEMENTS & SERVICES</div>
                <div onClick={()=>{setShowMenu(false);setShowVoicePay(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>🎤 Paiement vocal</div>
                <div onClick={()=>{setShowMenu(false);localStorage.getItem(TONTINE_CGU_KEY)==='1'?setShowTontine(true):setShowTontineCGU(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>🤝 Tontine / Njangi</div>
                <div onClick={()=>{setShowMenu(false);setShowQR(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>📲 QR Code</div>
                <div onClick={()=>{setShowMenu(false);setShowServices(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>💳 Recharge & Factures</div>
                <div onClick={()=>{setShowMenu(false);setShowMarketplace(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>🛒 Marketplace</div>
                <div onClick={()=>{setShowMenu(false);localStorage.getItem(INTL_CGU_KEY)==='1'?setShowIntlTransfer(true):setShowIntlCGU(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>🌍 Transfert International</div>
                <div style={{borderTop:`1px solid ${C.border}`,margin:'4px 0'}}/>
                <div style={{fontSize:11,color:C.muted,fontWeight:700,padding:'6px 14px',letterSpacing:1}}>COMPTE</div>
                <div onClick={()=>{setShowMenu(false);setShowPinSetup(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>🔑 Modifier PIN</div>
                <div onClick={()=>{setShowMenu(false);setShowSecurity(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>🔐 Sécurité & Appareils</div>
                <div onClick={async()=>{try{const r=await api.get('/face/status');setFaceAuthMode(r.registered?'verify':'register');}catch(e){setFaceAuthMode('register');}setShowFaceAuth(true);setShowMenu(false);}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:8,cursor:'pointer',color:C.text,fontSize:14}}>🤳 Reconnaissance faciale</div>
              </div>
              </>
            )}
          </div>

        </div>
      </div>
      <StoriesBar/>
      <div style={{padding:'10px 16px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px 12px',display:'flex',gap:8,alignItems:'center'}}>
          <span style={{color:C.muted}}>🔍</span>
          {'contacts' in navigator && 'ContactsManager' in window && (
            <button onClick={importPhoneContacts} title="Importer depuis les contacts du téléphone"
              style={{background:'none',border:'none',cursor:'pointer',fontSize:18,padding:'0 6px',color:'#00D4AA'}}>
              📋
            </button>
          )}
          <input value={searchQuery} onChange={e=>handleSearch(e.target.value)} placeholder={t('search_user')}
            onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();}}}
            style={{background:'none',border:'none',outline:'none',color:C.text,fontSize:14,width:'100%'}}/>
        </div>
        {searchQuery.length>=2&&searchResults.length===0&&(
          <div style={{marginTop:8,padding:'16px',textAlign:'center',color:C.muted,fontSize:13}}>
            Aucun utilisateur trouvé pour « {searchQuery} »
          </div>
        )}
        {searchResults.length>0&&(
          <div style={{marginTop:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
            {searchResults.map(u=>(
              <div key={u.id} style={{padding:'12px',display:'flex',alignItems:'center',gap:10,borderBottom:`1px solid ${C.border}`}}>
                <div style={{cursor:'pointer',display:'flex',alignItems:'center',gap:10,flex:1}} onClick={()=>startConv(u.id)}>
                  <Avatar name={u.display_name} color={getColor(u.id)} size={36}/>
                  <div>
                    <div style={{fontSize:14,fontWeight:600}}>{u.display_name}</div>
                    <div style={{fontSize:12,color:C.muted}}>@{u.username}</div>
                  </div>
                </div>
                <button onClick={()=>{shareContact(u);toast.success('Contact partage dans la conversation active');}} style={{background:C.accentDim,border:`1px solid ${C.accent}44`,borderRadius:8,padding:'4px 8px',color:C.accent,fontSize:11,cursor:'pointer',flexShrink:0}}>
                  Partager
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{flex:1,overflowY:'auto',WebkitOverflowScrolling:'touch'}}>
        {conversations.length===0 && searchQuery.length===0 &&(
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'60px 32px',gap:24,textAlign:'center'}}>
            <div style={{width:100,height:100,borderRadius:32,background:'linear-gradient(135deg,#00D4AA22,#00D4AA08)',border:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:48}}>
              💬
            </div>
            <div>
              <div style={{fontSize:20,fontWeight:700,color:C.text,marginBottom:8}}>Aucune conversation</div>
              <div style={{fontSize:14,color:C.muted,lineHeight:1.6,maxWidth:260}}>Cherchez un utilisateur par nom ou username pour démarrer une conversation.</div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:10,width:'100%',maxWidth:280}}>
              <button onClick={()=>{ const el=document.querySelector('input[placeholder]'); el?.focus(); }}
                style={{padding:'14px 0',background:`linear-gradient(90deg,${C.accent},#00b894)`,border:'none',borderRadius:14,color:'#000',fontWeight:700,fontSize:15,cursor:'pointer',boxShadow:`0 4px 20px ${C.accent}33`}}>
                🔍 Trouver un contact
              </button>
              <button onClick={()=>{ const shareData={title:'PayChat',text:'Rejoins-moi sur PayChat !',url:'https://zenvoi.app'}; navigator.share?.(shareData).catch(()=>{}); }}
                style={{padding:'13px 0',background:'none',border:`1px solid ${C.border}`,borderRadius:14,color:C.muted,fontSize:14,cursor:'pointer'}}>
                📤 Inviter un ami
              </button>
            </div>
          </div>
        )}
        {/* Bouton archives */}
        {conversations.filter(c=>c.is_archived).length>0&&(
          <div onClick={()=>setShowArchived(s=>!s)}
            style={{padding:'10px 16px',display:'flex',alignItems:'center',gap:10,cursor:'pointer',color:C.muted,fontSize:13,borderBottom:`1px solid ${C.border}22`}}>
            <span>📦</span>
            <span>Archivées ({conversations.filter(c=>c.is_archived).length})</span>
            <span style={{marginLeft:'auto'}}>{showArchived?'▲':'▼'}</span>
          </div>
        )}

        {/* Options contextuelle */}
        {convOptions&&(
          <div style={{position:'fixed',inset:0,zIndex:400,background:'#00000077'}} onClick={()=>setConvOptions(null)}>
            <div style={{position:'fixed',bottom:0,left:0,right:0,background:'#111827',borderRadius:'16px 16px 0 0',padding:20}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:15,fontWeight:700,color:'#E8EDF5',marginBottom:16,textAlign:'center'}}>{convOptions.name}</div>
              <div onClick={()=>{archiveConversation(convOptions.id,!convOptions.is_archived);setConvOptions(null);}}
                style={{padding:'14px 0',borderBottom:'1px solid #1e2d45',cursor:'pointer',color:'#E8EDF5',fontSize:14,display:'flex',alignItems:'center',gap:12}}>
                <span>{convOptions.is_archived?'📬':'📦'}</span>
                <span>{convOptions.is_archived?'Désarchiver':'Archiver'}</span>
              </div>
              <div onClick={()=>{muteConversation(convOptions.id,!convOptions.is_muted);setConvOptions(null);}}
                style={{padding:'14px 0',borderBottom:'1px solid #1e2d45',cursor:'pointer',color:'#E8EDF5',fontSize:14,display:'flex',alignItems:'center',gap:12}}>
                <span>{convOptions.is_muted?'🔔':'🔕'}</span>
                <span>{convOptions.is_muted?'Activer notifications':'Mettre en sourdine'}</span>
              </div>
              <div onClick={()=>setConvOptions(null)}
                style={{padding:'14px 0',cursor:'pointer',color:'#FF4F6A',fontSize:14,textAlign:'center',fontWeight:600}}>
                Annuler
              </div>
            </div>
          </div>
        )}

        {searchQuery.length === 0 && conversations.filter(c=>showArchived?c.is_archived:!c.is_archived).map(conv=>{
          const name=conv.type==='group'?conv.name:conv.contact_name;
          return (
            <div key={conv.id}
              onClick={e=>{if(longPressRef._fired){longPressRef._fired=false;return;}openConv(conv.id);}}
              onContextMenu={e=>e.preventDefault()}
              onTouchStart={e=>{
                longPressRef._fired=false;
                const sy=e.touches[0].clientY,sx=e.touches[0].clientX;
                longPressRef.current={timer:setTimeout(()=>{
                  longPressRef._fired=true;
                  longPressRef.current=null;
                  setConvOptions({...conv,name});
                },800),sy,sx};
              }}
              onTouchMove={e=>{
                if(!longPressRef.current)return;
                if(Math.abs(e.touches[0].clientY-longPressRef.current.sy)>6||Math.abs(e.touches[0].clientX-longPressRef.current.sx)>6){
                  clearTimeout(longPressRef.current.timer);longPressRef.current=null;
                }
              }}
              onTouchEnd={()=>{if(longPressRef.current){clearTimeout(longPressRef.current.timer);longPressRef.current=null;}}}
              style={{padding:'14px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:12,borderBottom:`1px solid ${C.border}22`}}>
              <Avatar name={name||''} color={getColor(conv.contact_id||conv.id)} size={56} online={conv.type==='direct'?!!onlineUsers[conv.contact_id]:undefined} avatarUrl={conv.contact_avatar_url}/>
              <div style={{flex:1,overflow:'hidden'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                  <span style={{fontWeight:700,fontSize:15}}>
                    {conv.is_muted&&<span style={{fontSize:12,marginRight:4}}>🔕</span>}
                    {name}
                  </span>
                  <span style={{fontSize:11,color:C.muted,flexShrink:0,marginLeft:8}}>{timeAgo(conv.last_message_at)}</span>
                </div>
                <div style={{fontSize:13,color:C.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{lastMsg(conv)}</div>
              </div>
              <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,flexShrink:0}}>
                {conv.unread_count>0&&<div style={{background:C.accent,color:'#000',borderRadius:10,minWidth:20,height:20,padding:'0 4px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800}}>{conv.unread_count>99?'99+':conv.unread_count}</div>}
                <button onClick={e=>{e.stopPropagation();e.preventDefault();setConvOptions({...conv,name});}}
                  style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:16,cursor:'pointer',padding:'4px 8px',lineHeight:1,flexShrink:0}}>⋮</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
    <BottomNav/>
    </>
  );

  if (view === 'chat' && !activeConv) {
    return (
      <div style={{position:'fixed',inset:0,background:'#0A0E1A',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16}}>
        <div style={{color:'#FF4F6A',fontSize:16,fontWeight:700}}>Conversation introuvable</div>
        <button onClick={()=>setView('list')} style={{padding:'10px 20px',background:'#00D4AA',border:'none',borderRadius:10,cursor:'pointer',color:'#000',fontWeight:700}}>Retour</button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <Suspense fallback={null}>
    <>
    {showVoicePay && <VoicePayPage onClose={()=>setShowVoicePay(false)} conversations={conversations}/>}
    {showPinSetup && <PinSetupPage onClose={()=>setShowPinSetup(false)} onSuccess={()=>setShowPinSetup(false)}/>}
    {showProfile && <Suspense fallback={null}><ProfilePage onClose={()=>setShowProfile(false)}/></Suspense>}
    {showCallsHistory && <CallsHistoryPage onClose={()=>setShowCallsHistory(false)} user={user}/>}
    {docViewer && (
      <div style={{position:'fixed',inset:0,background:'#0A0E1A',zIndex:500,display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'16px 20px',background:'#111827',borderBottom:`1px solid #1e2d45`}}>
          <button onClick={()=>setDocViewer(null)} style={{background:'none',border:'none',color:'#6b7fa3',fontSize:22,cursor:'pointer'}}>←</button>
          <div style={{flex:1,fontSize:14,fontWeight:600,color:'#E8EDF5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{docViewer.filename||'Document'}</div>
          <a href={docViewer.url} download={docViewer.filename} style={{background:'#00D4AA',color:'#000',border:'none',borderRadius:8,padding:'6px 12px',fontSize:12,fontWeight:700,textDecoration:'none'}}>⬇ Télécharger</a>
        </div>
        <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:20,padding:24}}>
          <div style={{fontSize:64}}>📄</div>
          <div style={{fontSize:16,fontWeight:600,color:'#E8EDF5',textAlign:'center'}}>{docViewer.filename}</div>
          <div style={{fontSize:13,color:'#6b7fa3'}}>{docViewer.bytes ? (docViewer.bytes/1024/1024).toFixed(1)+' MB' : ''}</div>
          <a href={docViewer.url} target='_blank' rel='noopener noreferrer' style={{background:'linear-gradient(90deg,#00D4AA,#00b894)',color:'#000',borderRadius:12,padding:'14px 28px',fontSize:15,fontWeight:700,textDecoration:'none'}}>Ouvrir le document</a>
        </div>
      </div>
    )}
    {incomingCall && <IncomingCall call={incomingCall} onAccept={acceptCall} onReject={rejectCall}/>}
    {showFaceAuth && <FaceAuthPage mode={faceAuthMode} onSuccess={()=>{setShowFaceAuth(false);toast.success('Identité vérifiée !');}} onCancel={()=>setShowFaceAuth(false)}/>}
    <div style={{...S,position:'fixed',inset:0,background:C.bg,display:'flex',flexDirection:'column',overflow:'hidden'}}>
      <style>{CSS}</style>
      <div style={{padding:'10px 12px',background:C.surface,borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
        <button onClick={goBack} style={{background:'none',border:'none',color:C.accent,fontSize:26,cursor:'pointer',lineHeight:1,padding:'0 4px',flexShrink:0}}>&#8249;</button>
        <Avatar name={activeConv?.contact_name||activeConv?.name||''} color={getColor(activeConv?.contact_id)} size={38} online={!!onlineUsers[activeConv?.contact_id]} avatarUrl={activeConv?.contact_avatar_url}/>
        <div style={{flex:1,overflow:'hidden'}}>
          <div style={{fontWeight:700,fontSize:15,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{activeConv?.contact_name||activeConv?.name}</div>
          {!Object.keys(typing).length&&(activeConv?.contact_id)&&<div style={{fontSize:11,color:onlineUsers[activeConv?.contact_id]?C.accent:C.muted,marginTop:1}}>{lastSeenText(activeConv?.contact_last_seen,!!onlineUsers[activeConv?.contact_id])}</div>}
          {Object.keys(typing).length>0&&<div style={{fontSize:11,color:C.accent}}>{activeConv?.type==='group'?Object.values(typing).join(', ')+' écrit...':"En train d'écrire..."}</div>}
        </div>
        
        <button onClick={()=>startCall('audio')} style={{background:'#ffffff11',border:'none',borderRadius:10,width:36,height:36,cursor:'pointer',fontSize:18,flexShrink:0}}>📞</button>
        <button onClick={()=>startCall('video')} style={{background:'#ffffff11',border:'none',borderRadius:10,width:36,height:36,cursor:'pointer',fontSize:18,flexShrink:0}}>📹</button>
        <button onClick={()=>setShowPayModal(true)} style={{background:`linear-gradient(135deg,${C.accent},#00b894)`,border:'none',borderRadius:10,width:36,height:36,cursor:'pointer',fontSize:16,flexShrink:0}}>💸</button>
        <div style={{position:'relative',flexShrink:0}}>
          <button onClick={()=>setShowHeaderMenu(s=>!s)} style={{background:'#ffffff11',border:'none',borderRadius:10,width:36,height:36,cursor:'pointer',fontSize:20,color:C.text}}>⋯</button>
          {showHeaderMenu && (
            <div onClick={()=>setShowHeaderMenu(false)} style={{position:'fixed',inset:0,zIndex:9998}}/>
          )}
          {showHeaderMenu && (
            <div style={{position:'absolute',top:42,right:0,background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:8,zIndex:9999,minWidth:200,boxShadow:'0 8px 32px #0008'}}>
              <div onClick={()=>{setShowVoicePay(true);setShowHeaderMenu(false);}} style={{padding:'10px 16px',cursor:'pointer',color:C.text,fontSize:14,display:'flex',alignItems:'center',gap:10,borderRadius:8}}>🎤💸 Paiement vocal</div>
              <div onClick={()=>{setShowEphemeralMenu(s=>!s);setShowHeaderMenu(false);}} style={{padding:'10px 16px',cursor:'pointer',color:ephemeralDuration>0?C.accent:C.text,fontSize:14,display:'flex',alignItems:'center',gap:10,borderRadius:8}}>⏱ Messages éphémères</div>
              {activeConv?.type==='group'&&<div onClick={()=>{setShowGroupInfo(true);loadGroupParticipants(activeConversationId);setShowHeaderMenu(false);}} style={{padding:'10px 16px',cursor:'pointer',color:C.text,fontSize:14,display:'flex',alignItems:'center',gap:10,borderRadius:8}}>👥 Infos du groupe</div>}
            </div>
          )}
        </div>
      </div>
      <div style={{flex:1,overflowY:'auto',WebkitOverflowScrolling:'touch',padding:'12px',display:'flex',flexDirection:'column',gap:6}}>
        {currentMessages.map(msg=>{
          const fromMe=msg.sender_id===user?.id;
          if (msg.is_deleted) return (
            <div key={msg.id} style={{display:'flex',justifyContent:fromMe?'flex-end':'flex-start'}}>
              <div style={{fontSize:12,color:C.muted,fontStyle:'italic',padding:'6px 12px',background:C.card,borderRadius:12}}>Message supprimé</div>
            </div>
          );
          return (
            <div key={msg.id} style={{display:'flex',flexDirection:fromMe?'row-reverse':'row',alignItems:'flex-end',gap:6}}>
              {!fromMe&&<Avatar name={msg.sender_name||''} color={getColor(msg.sender_id)} size={26} avatarUrl={msg.sender_avatar_url}/>}
              <div style={{maxWidth:'80%'}}>
                <MessageBubble msg={msg} fromMe={fromMe} onReply={setReplyTo} onDelete={handleDelete} onReact={handleReact} onForward={setForwardMsg} onEdit={setEditingMsg} decryptedText={decryptedCache[msg.id] || undefined}/>
                <div style={{fontSize:10,color:C.muted,marginTop:2,textAlign:fromMe?'right':'left'}}>
                  {msg.expires_at&&<span style={{color:'#F59E0B',marginRight:4}}>⏱</span>}
                  {timeAgo(msg.created_at)||'maintenant'}{fromMe&&(
                    msg.isTemp
                      ? <span style={{color:C.muted}}> ⏳</span>
                      : msg.is_read
                        ? <span style={{color:C.accent}} title="Lu"> ✓✓</span>
                        : <span style={{color:C.muted}} title="Envoyé"> ✓</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef}/>
      </div>
      <div style={{background:C.surface,borderTop:`1px solid ${C.border}`,padding:'8px 10px',paddingBottom:'calc(8px + 64px)',flexShrink:0}}>
        {showStickers&&(
          <div style={{background:C.surface,borderTop:`1px solid ${C.border}`,padding:12}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:8,fontWeight:600}}>STICKERS PAYCHAT</div>
            <div style={{display:'flex',gap:8,overflowX:'auto',paddingBottom:4}}>
              {['ok.svg','merci.svg','haha.svg','love.svg','bravo.svg','non.svg','paychat.svg','cameroun.svg','surprise.svg','triste.svg','colere.svg','fatigue.svg','amoureux.svg','afrique.svg','soleil.svg','tamtam.svg','paiement_recu.svg','wallet.svg','argent.svg','bonjour.svg','bonsoir.svg','bonne_nuit.svg','bienvenue.svg'].map(s=>(
                <img key={s} src={'/stickers/pack1/'+s} alt={s}
                  onClick={()=>sendSticker(s)}
                  style={{width:60,height:60,cursor:'pointer',borderRadius:10,flexShrink:0,background:C.card,padding:4}}/>
              ))}
            </div>
          </div>
        )}
        {editingMsg&&(
        <div style={{padding:'8px 12px',background:'#003d2e',borderTop:`1px solid ${C.accent}44`,display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:12,color:C.accent}}>✏️ Modifier le message</span>
          <span style={{flex:1,fontSize:12,color:C.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{editingMsg.content}</span>
          <button onClick={()=>setEditingMsg(null)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:16}}>✕</button>
        </div>
      )}
      {replyTo&&(
          <div style={{background:C.card,border:`1px solid ${C.accent}44`,borderRadius:10,padding:'8px 12px',marginBottom:8,display:'flex',alignItems:'center',gap:8}}>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:C.accent}}>↩ Repondre</div>
              <div style={{fontSize:12,color:C.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{String(replyTo.content||'').substring(0,60)}</div>
            </div>
            <button onClick={()=>setReplyTo(null)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:16}}>✕</button>
          </div>
        )}
        {imagePreview&&(
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:10,marginBottom:8,display:'flex',gap:10}}>
            <img src={imagePreview.url} style={{width:64,height:64,objectFit:'cover',borderRadius:8,flexShrink:0}}/>
            <div style={{flex:1}}>
              <input value={imageCaption} onChange={e=>setImageCaption(e.target.value)} placeholder="Legende..." style={{width:'100%',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 10px',color:C.text,fontSize:13,outline:'none',marginBottom:6}}/>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>setImagePreview(null)} style={{padding:'5px 12px',background:C.card,border:`1px solid ${C.border}`,borderRadius:8,cursor:'pointer',color:C.muted,fontSize:12}}>Annuler</button>
                <button onClick={sendImage} disabled={isUploading} style={{padding:'5px 14px',background:`linear-gradient(90deg,${C.accent},#00b894)`,border:'none',borderRadius:8,cursor:'pointer',color:'#000',fontWeight:700,fontSize:12,opacity:isUploading?0.6:1}}>
                  {isUploading?'Envoi...':'Envoyer'}
                </button>
              </div>
            </div>
          </div>
        )}
        {audioBlob&&!isRecording&&(
          <div style={{background:C.card,border:`1px solid ${C.accent}44`,borderRadius:12,padding:'8px 12px',marginBottom:8,display:'flex',alignItems:'center',gap:8}}>
            <span>🎤</span>
            <audio src={URL.createObjectURL(audioBlob)} controls style={{flex:1,height:28}}/>
            <button onClick={()=>setAudioBlob(null)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:16}}>✕</button>
            <button onClick={sendAudio} style={{padding:'5px 10px',background:`linear-gradient(90deg,${C.accent},#00b894)`,border:'none',borderRadius:8,cursor:'pointer',color:'#000',fontWeight:700,fontSize:12}}>Envoyer</button>
          </div>
        )}
        {isRecording&&(
          <div style={{background:'#1a0010',border:`1px solid ${C.red}44`,borderRadius:12,padding:'8px 12px',marginBottom:8,display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:8,height:8,borderRadius:'50%',background:C.red,animation:'pulse 1s infinite',flexShrink:0}}/>
            <span style={{color:C.red,fontSize:13,fontWeight:600}}>Enregistrement</span>
            <span style={{color:C.muted,fontSize:12,marginLeft:'auto'}}>{Math.floor(recSeconds/60)}:{(recSeconds%60).toString().padStart(2,'0')}</span>
            <button onClick={cancelRec} style={{background:'none',border:`1px solid ${C.border}`,borderRadius:6,padding:'3px 8px',cursor:'pointer',color:C.muted,fontSize:12}}>✕</button>
            <button onClick={stopRec} style={{background:C.red,border:'none',borderRadius:6,padding:'5px 10px',cursor:'pointer',color:'#fff',fontWeight:700,fontSize:12}}>Stop</button>
          </div>
        )}
        {!imagePreview&&!audioBlob&&!isRecording&&input.length>0&&(
          <div style={{display:'flex',gap:6,marginBottom:6,paddingLeft:4,alignItems:'center'}}>
            <span onClick={()=>setShowFormat(s=>!s)} style={{fontSize:11,color:showFormat?C.accent:C.muted,cursor:'pointer',padding:'2px 6px',borderRadius:6,background:showFormat?C.accent+'22':'transparent'}}>Aa</span>
            {showFormat && [['b','*B*','*'],['i','_I_','_'],['s','~S~','~']].map(([tag,label,marker])=>(
              <button key={tag} onClick={()=>{
                applyFormat(tag);
                setTimeout(()=>{try{textareaRef.current?.focus();}catch(e){}},50);
              }}
                style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:'4px 10px',cursor:'pointer',color:C.accent,fontSize:12,fontFamily:'monospace'}}>
                {tag==='b'?<strong>B</strong>:tag==='i'?<em>I</em>:<del>S</del>}
              </button>
            ))}
            <span style={{fontSize:10,color:C.muted}}>ex: *gras* _italique_</span>
          </div>
        )}
        {!imagePreview&&!audioBlob&&!isRecording&&(
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <input ref={fileInputRef} type="file" accept="image/*,video/*" onChange={e=>{const f=e.target.files?.[0];if(!f)return;if(f.type.startsWith('video/'))sendMedia(f,'video');else handleImageSelect(e);}} style={{display:'none'}}/>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleImageSelect} style={{display:'none'}}/>
            <input ref={videoCaptureRef} type="file" accept="video/*" capture="environment" onChange={e=>{const f=e.target.files?.[0];if(f)sendMedia(f,'video');e.target.value='';}} style={{display:'none'}}/>
            <div style={{position:'relative'}}>
              <button onClick={()=>setShowMediaMenu(m=>!m)} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,width:38,height:38,cursor:'pointer',fontSize:18,flexShrink:0}}>📷</button>
              {showMediaMenu&&(
                <div style={{position:'absolute',bottom:'110%',left:0,background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',minWidth:180,boxShadow:'0 -4px 20px #00000044',zIndex:100}} onClick={()=>setShowMediaMenu(false)}>
                  <div onClick={()=>cameraRef.current?.click()} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',color:C.text,fontSize:14,borderBottom:`1px solid ${C.border}`}}>📸 Prendre une photo</div>
                  <div onClick={()=>videoCaptureRef.current?.click()} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',color:C.text,fontSize:14,borderBottom:`1px solid ${C.border}`}}>🎥 Filmer une vidéo</div>
                  <div onClick={()=>fileInputRef.current?.click()} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',color:C.text,fontSize:14,borderBottom:`1px solid ${C.border}`}}>🖼 Photothèque / Galerie</div>
                  <div onClick={()=>docFileRef.current?.click()} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',color:C.text,fontSize:14}}>📄 Fichier / Document</div>
                  <div onClick={()=>{setShowMediaMenu(false);shareLocation();}} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',color:C.text,fontSize:14}}>📍 Partager ma position</div>
<div onClick={()=>{setShowMediaMenu(false);setShowPollCreator(true);}} style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',color:C.text,fontSize:14}}>📊 Créer un sondage</div>
                </div>
              )}
            </div>
            <input ref={docFileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={e=>{const f=e.target.files?.[0];if(f)sendMedia(f,'document');e.target.value='';}} style={{display:'none'}}/>
            
            <textarea ref={textareaRef} value={input} 
              onSelect={e=>{selectionRef.current={start:e.target.selectionStart,end:e.target.selectionEnd};}}
              onKeyUp={e=>{selectionRef.current={start:e.target.selectionStart,end:e.target.selectionEnd};}}
              onChange={e=>{setInput(e.target.value);if(activeConversationId)socketService.sendTypingStart(activeConversationId);}}
              onKeyDown={e=>{if(e.key==='Enter'&&(e.ctrlKey||e.metaKey)){e.preventDefault();handleSend();}}}
              placeholder={t('message_placeholder')}
              rows={1}
              enterKeyHint="send"
              onCompositionEnd={e=>{}}
              style={{flex:1,background:C.card,border:`1px solid ${C.border}`,borderRadius:20,padding:'10px 14px',color:C.text,fontSize:15,outline:'none',minWidth:0,resize:'none',fontFamily:'inherit',maxHeight:120,overflowY:'auto'}}/>
            <button onClick={startDictation} title={isDictating?'Arrêter la dictée':'Dicter un message'}
              style={{background:isDictating?'#FF4F6A22':C.card,border:`1px solid ${isDictating?'#FF4F6A':C.border}`,borderRadius:10,width:38,height:38,cursor:'pointer',fontSize:16,flexShrink:0,color:isDictating?'#FF4F6A':C.muted}}>
              {isDictating?'⏹️':'🎙️'}
            </button>
            <button onClick={()=>setShowStickers(s=>!s)} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,width:38,height:38,cursor:'pointer',fontSize:18,flexShrink:0}}>😊</button>
            <button onClick={startRec} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,width:38,height:38,cursor:'pointer',fontSize:18,flexShrink:0}}>🎤</button>
            <button onClick={handleSend} style={{background:`linear-gradient(135deg,${C.accent},#00b894)`,border:'none',borderRadius:10,width:38,height:38,cursor:'pointer',fontSize:16,flexShrink:0}}>&#9658;</button>
          </div>
        )}
      </div>
      {/* Menu éphémère */}
      {showEphemeralMenu && (
        <div style={{position:'fixed',top:70,right:16,background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,zIndex:400,minWidth:200,boxShadow:'0 8px 32px #00000088',overflow:'hidden'}}>
          <div style={{padding:'10px 16px',fontSize:12,color:C.muted,fontWeight:600,borderBottom:`1px solid ${C.border}`}}>⏱ Messages éphémères</div>
          {[[0,'Désactivé'],[300,'5 minutes'],[3600,'1 heure'],[86400,'24 heures'],[604800,'7 jours']].map(([sec,label])=>(
            <div key={sec} onClick={()=>setEphemeral(sec)}
              style={{padding:'12px 16px',cursor:'pointer',color:ephemeralDuration===sec?C.accent:C.text,background:ephemeralDuration===sec?C.accent+'11':'transparent',fontSize:14,display:'flex',alignItems:'center',gap:8,borderBottom:`1px solid ${C.border}`}}>
              {ephemeralDuration===sec&&<span style={{color:C.accent}}>✓</span>}
              {label}
            </div>
          ))}
        </div>
      )}
      {showEphemeralMenu&&<div style={{position:'fixed',inset:0,zIndex:399}} onClick={()=>setShowEphemeralMenu(false)}/>}
      {showPollCreator&&<PollCreator conversationId={activeConversationId} onClose={()=>setShowPollCreator(false)} onCreated={()=>setShowPollCreator(false)} />}

      {showPayModal&&(
        <PayInlineModal
          contact={activeConv}
          wallet={wallet}
          conversationId={activeConversationId}
          onClose={()=>setShowPayModal(false)}
          onSuccess={(amount, txData)=>{
            // Injecter le message payment immédiatement dans le fil sans attendre le socket
            if (txData && activeConversationId) {
              const { useMessageStore } = require('../store');
              useMessageStore.getState().addMessage({
                id: txData.id || ('tmp-pay-' + Date.now()),
                conversation_id: activeConversationId,
                sender_id: user?.id,
                type: 'payment',
                content: JSON.stringify({
                  transaction_id: txData.id,
                  amount: txData.amount,
                  fee: txData.fee,
                  net_amount: txData.net_amount,
                  currency: txData.currency || 'XAF',
                  reference: txData.reference,
                }),
                created_at: new Date().toISOString(),
              });
            }
            setShowPayModal(false);
            fetchWallet();
            toast.success(`💸 ${fmt(amount)} envoyés à ${activeConv?.contact_name} !`);
          }}
        />
      )}
    </div>
    </>
    </Suspense>
    </ErrorBoundary>
  );
}

// ============================================
// COMPOSANT APERCU LIEN
// ============================================
function LinkPreview({ url }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    fetch('/api/meta/preview?url=' + encodeURIComponent(url), {
      headers: { Authorization: 'Bearer ' + token }
    }).then(r => r.json()).then(d => {
      if (d.success && (d.data.title || d.data.image)) setPreview(d.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [url]);

  if (loading || !preview) return null;

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{textDecoration:'none',display:'block',marginTop:8}}>
      <div style={{background:'#0A0E1A',border:'1px solid #1e2d45',borderRadius:10,overflow:'hidden',maxWidth:280}}>
        {preview.image && (
          <img src={preview.image} alt="" style={{width:'100%',height:140,objectFit:'cover',display:'block'}}
            onError={e=>e.target.style.display='none'} />
        )}
        <div style={{padding:'8px 12px'}}>
          <div style={{fontSize:11,color:'#00D4AA',marginBottom:4}}>{preview.domain}</div>
          {preview.title && <div style={{fontSize:13,fontWeight:600,color:'#E8EDF5',marginBottom:4,lineHeight:1.4}}>{preview.title.substring(0,80)}</div>}
          {preview.description && <div style={{fontSize:12,color:'#6b7fa3',lineHeight:1.4}}>{preview.description.substring(0,120)}</div>}
        </div>
      </div>
    </a>
  );
}

// ============================================
// COMPOSANT SONDAGE — AFFICHAGE
// ============================================
function PollBubble({ pollId, question, options, fromMe, bg, border, br, liveResults }) {
  const [results, setResults] = useState([]);
  const [userVotes, setUserVotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuthStore();

  useEffect(() => {
    if (!pollId) return;
    fetch('/api/polls/'+pollId, {
      headers: { Authorization: 'Bearer '+localStorage.getItem('access_token') }
    }).then(r=>r.json()).then(d=>{
      if (d.success) { setResults(d.data.results||[]); setUserVotes(d.data.userVotes||[]); }
    }).catch(()=>{});
  }, [pollId]);

  // Mise à jour temps réel via socket
  useEffect(() => {
    if (liveResults) setResults(liveResults);
  }, [liveResults]);

  const vote = async (idx) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch('/api/polls/'+pollId+'/vote', {
        method:'POST',
        headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('access_token')},
        body: JSON.stringify({ option_index: idx })
      });
      const d = await res.json();
      if (d.success) { setResults(d.data.results||[]); setUserVotes(v => [...v.filter(v=>v!==idx), idx]); }
    } catch {}
    finally { setLoading(false); }
  };

  const totalVotes = results.reduce((sum, r) => sum + parseInt(r.count||0), 0);

  return (
    <div style={{background:bg, border, borderRadius:br, padding:'12px 14px', minWidth:220, maxWidth:300}}>
      <div style={{fontSize:12, color:'#00D4AA', fontWeight:700, marginBottom:8}}>📊 Sondage</div>
      <div style={{fontSize:14, fontWeight:600, color:'#E8EDF5', marginBottom:12, lineHeight:1.4}}>{question}</div>
      {(options||[]).map((opt, idx) => {
        const result = results.find(r => parseInt(r.option_index) === idx);
        const count = parseInt(result?.count||0);
        const pct = totalVotes > 0 ? Math.round((count/totalVotes)*100) : 0;
        const voted = userVotes.includes(idx);
        return (
          <div key={idx} onClick={()=>vote(idx)} style={{marginBottom:8,cursor:'pointer'}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span style={{fontSize:13,color:voted?'#00D4AA':'#E8EDF5',fontWeight:voted?700:400}}>{voted?'✓ ':''}{opt}</span>
              <span style={{fontSize:12,color:'#6b7fa3'}}>{pct}%</span>
            </div>
            <div style={{height:4,background:'#1e2d45',borderRadius:2,overflow:'hidden'}}>
              <div style={{height:'100%',background:voted?'#00D4AA':'#2E7D6B',width:pct+'%',borderRadius:2,transition:'width 0.3s'}}/>
            </div>
          </div>
        );
      })}
      <div style={{fontSize:11,color:'#6b7fa3',marginTop:8}}>{totalVotes} vote{totalVotes!==1?'s':''}</div>
    </div>
  );
}

// ============================================
// COMPOSANT SONDAGE — CRÉATION
// ============================================
function PollCreator({ conversationId, onClose, onCreated }) {
  const C2 = { bg:'#0A0E1A', card:'#0D1526', accent:'#00D4AA', border:'#1e2d45', text:'#E8EDF5', muted:'#6b7fa3' };
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['','']);
  const [isMultiple, setIsMultiple] = useState(false);
  const [loading, setLoading] = useState(false);

  const addOption = () => { if (options.length < 10) setOptions(o => [...o, '']); };
  const removeOption = (i) => { if (options.length > 2) setOptions(o => o.filter((_,idx) => idx !== i)); };
  const updateOption = (i, val) => setOptions(o => o.map((v,idx) => idx===i ? val : v));

  const submit = async () => {
    if (!question.trim()) return;
    const validOptions = options.filter(o => o.trim());
    if (validOptions.length < 2) return;
    setLoading(true);
    try {
      const res = await fetch('/api/polls', {
        method:'POST',
        headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('access_token')},
        body: JSON.stringify({ conversation_id: conversationId, question: question.trim(), options: validOptions, is_multiple: isMultiple })
      });
      const d = await res.json();
      if (d.success) {
        // Ajouter le message poll dans le store local
        if (d.data?.message) {
          const { useMessageStore } = require('../store');
          useMessageStore.getState().addMessage({
            ...d.data.message,
            content: typeof d.data.message.content === 'string' 
              ? JSON.parse(d.data.message.content) 
              : d.data.message.content
          });
        }
        onCreated();
      }
      else alert(d.error);
    } catch(e) { alert('Erreur: '+e.message); }
    finally { setLoading(false); }
  };

  const inp = { width:'100%', background:C2.card, border:'1px solid '+C2.border, borderRadius:10, padding:'10px 14px', color:C2.text, fontSize:14, outline:'none', boxSizing:'border-box' };

  return (
    <div style={{position:'fixed',inset:0,background:C2.bg,zIndex:500,display:'flex',flexDirection:'column'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,padding:'16px 20px',borderBottom:'1px solid '+C2.border}}>
        <button onClick={onClose} style={{background:'none',border:'none',color:C2.muted,fontSize:22,cursor:'pointer'}}>←</button>
        <div style={{fontSize:17,fontWeight:700,color:C2.text}}>📊 Créer un sondage</div>
        <button onClick={submit} disabled={loading} style={{marginLeft:'auto',background:C2.accent,border:'none',borderRadius:20,padding:'8px 20px',color:'#000',fontWeight:700,cursor:'pointer',opacity:loading?0.6:1}}>
          {loading?'...':'Publier'}
        </button>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:20}}>
        <div style={{fontSize:12,color:C2.muted,marginBottom:6}}>QUESTION</div>
        <textarea style={{...inp,minHeight:80,resize:'none',marginBottom:20,lineHeight:1.5}}
          placeholder="Posez votre question..." value={question} onChange={e=>setQuestion(e.target.value)} />

        <div style={{fontSize:12,color:C2.muted,marginBottom:10}}>OPTIONS ({options.length}/10)</div>
        {options.map((opt,i)=>(
          <div key={i} style={{display:'flex',gap:8,marginBottom:10,alignItems:'center'}}>
            <input style={{...inp,flex:1}} placeholder={`Option ${i+1}`} value={opt} onChange={e=>updateOption(i,e.target.value)} />
            {options.length>2&&<button onClick={()=>removeOption(i)} style={{background:'none',border:'none',color:'#FF4F6A',fontSize:20,cursor:'pointer',flexShrink:0}}>✕</button>}
          </div>
        ))}
        {options.length<10&&(
          <button onClick={addOption} style={{width:'100%',background:C2.card,border:'1px dashed '+C2.border,borderRadius:10,padding:12,color:C2.accent,fontWeight:600,cursor:'pointer',marginBottom:20}}>
            + Ajouter une option
          </button>
        )}

        <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px',background:C2.card,borderRadius:10,border:'1px solid '+C2.border}}>
          <div style={{flex:1}}>
            <div style={{fontSize:14,color:C2.text,fontWeight:500}}>Choix multiple</div>
            <div style={{fontSize:12,color:C2.muted}}>Permettre plusieurs réponses</div>
          </div>
          <div onClick={()=>setIsMultiple(m=>!m)} style={{width:44,height:24,borderRadius:12,background:isMultiple?C2.accent:'#444',cursor:'pointer',position:'relative',transition:'background 0.2s'}}>
            <div style={{position:'absolute',top:2,left:isMultiple?20:2,width:20,height:20,borderRadius:10,background:'#fff',transition:'left 0.2s'}}/>
          </div>
        </div>
      </div>
    </div>
  );
}
